// 自重トレ大全 3D 種目プレーヤー（3D のキャラクターの名前は「JIJUくん」）
// - 人体は自前の骨格（骨盤・腰・胸・首・頭／肩→肘→手首／股関節→膝→足首）で組み、
//   手足は「どこに置くか（目標点）」と「肘・膝の向き（ポール）」から 2 関節 IK で解く。
//   接地点（手・つま先・かかと）を固定できるので、床から浮いたり、めり込んだりしにくい。
// - 種目の動きは jiju-moves.js の pose(t, P) が返す（t は 1 回の動作の 0〜1）。
// - three.js は使うときに CDN から読み込む（ページの初期表示を重くしない）。
// 使い方: <div class="jiju-3d" data-ex="push-up"></div> を置き、このファイルを module で読み込む。
//         図鑑ページのように切り替える場合は window.Jiju3D.mount(el, { ex }) → player.load(id)。

const THREE_URL = 'https://cdn.jsdelivr.net/npm/three@0.169.0/build/three.module.min.js';
let T = null;
let MOVES = null;
const movesUrl = import.meta.url.replace(/jiju-3d(\.min)?\.js/, 'jiju-moves.js');
const bodyUrl = import.meta.url.replace(/jiju-3d(\.min)?\.js/, 'jiju-body.js');
// 解説ナレーション（VOICEVOX:青山龍星。scripts/jiju/narrate.mjs が作る）。種目ごとに assets/jiju-voice-<id>.mp3
const voiceUrl = (id) => import.meta.url.replace(/jiju-3d(\.min)?\.js(\?.*)?$/, `jiju-voice-${id}.mp3`);
const VOICE_CREDIT = 'VOICEVOX:青山龍星';
// 解説ナレーションを出すか。青山龍星の利用規約で、企業の利用は ななはぴ への事前確認が必要。確認が取れたら true にして音声ファイルを置く
const NARRATION = false;
let VOICE = null; // いま話しているプレーヤー（同時に話すのは1つだけ）
let BODY = null;

async function deps(style) {
  if (!T) T = await import(THREE_URL);
  if (!MOVES) MOVES = (await import(movesUrl)).default;
  if (style !== 'mannequin' && !BODY) BODY = await import(bodyUrl);
  return { T, MOVES, BODY };
}

// ---------------------------------------------------------------- 体の寸法（m）
// 人体モデル（assets/jiju-body.js。scripts/jiju/build-model.mjs が MakeHuman の関節の位置から測る）と同じ値
export const DIM = {
  hipX: 0.1083, hipDrop: 0.0065, hipZ: -0.0007,   // 骨盤の中心 → 股関節
  abd: 0.1991, rib: 0.3523,                        // 骨盤 → 腰（みぞおちの裏）→ 首の付け根
  shX: 0.215, shY: -0.1036, shZ: 0.0192,           // 首の付け根 → 肩関節
  clvX: 0.0227, clvY: -0.0734, clvZ: 0.0175,          // 首の付け根 → 鎖骨の付け根（腕を上げると鎖骨と肩甲骨がここを軸に回る）
  headY: 0.1834, headZ: 0,                         // 首の付け根 → 頭の中心
  upper: 0.254, fore: 0.2627, hand: 0.2049,        // 上腕・前腕・手（手首から指先）
  thigh: 0.4315, shin: 0.4331, foot: 0.1438,       // 太もも・すね・足（足首から母指球）
  ankleH: 0.0712,                                  // 足裏を床につけたときの足首の高さ
  toe: 0.0483, ballH: 0.0275,                      // 足の指（母指球→指先）・立ったときの母指球の関節の高さ
  palmH: 0.0311,                                   // 手のひらを床につけたときの手首の関節の高さ
  knuck: 0.1081, fing1: 0.03, fing2: 0.0521,       // 手首→指の付け根、付け根→第2関節、第2関節→指先
  thumb: 0.1015,                                   // 親指（付け根→指先）
  standY: 0.9407,                                  // 立ったときの骨盤の中心の高さ
};
DIM.torso = DIM.abd + DIM.rib + DIM.shY; // 骨盤の中心 → 肩の高さ（まっすぐのとき）
DIM.leg = DIM.thigh + DIM.shin;
DIM.arm = DIM.upper + DIM.fore;

// ---------------------------------------------------------------- 配列のベクトル（動きの定義側で使う）
const A = {
  add: (a, b) => [a[0] + b[0], a[1] + b[1], a[2] + b[2]],
  sub: (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]],
  mul: (a, s) => [a[0] * s, a[1] * s, a[2] * s],
  dot: (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2],
  cross: (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]],
  len: (a) => Math.hypot(a[0], a[1], a[2]),
  norm: (a) => { const l = Math.hypot(a[0], a[1], a[2]) || 1; return [a[0] / l, a[1] / l, a[2] / l]; },
  lerp: (a, b, w) => [a[0] + (b[0] - a[0]) * w, a[1] + (b[1] - a[1]) * w, a[2] + (b[2] - a[2]) * w],
};
const clamp = (x, a, b) => Math.min(b, Math.max(a, x));
const smooth = (x) => { x = clamp(x, 0, 1); return x * x * (3 - 2 * x); };
const easeIO = (x) => { x = clamp(x, 0, 1); return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2; };
const lerpN = (a, b, w) => a + (b - a) * w;
const rad = (d) => d * Math.PI / 180;

// キーフレーム: K(t, [[0,a],[0.4,b],[1,a]]) → なめらかに補間（数値でも配列でも）
function K(t, keys, ease = easeIO) {
  if (t <= keys[0][0]) return keys[0][1];
  for (let i = 1; i < keys.length; i++) {
    if (t <= keys[i][0]) {
      const [t0, v0] = keys[i - 1], [t1, v1] = keys[i];
      const w = ease((t - t0) / Math.max(1e-6, t1 - t0));
      return Array.isArray(v0) ? v0.map((x, j) => lerpN(x, v1[j], w)) : lerpN(v0, v1, w);
    }
  }
  return keys[keys.length - 1][1];
}

// 動きの定義に渡す道具
export const P = { A, K, D: DIM, clamp, smooth, easeIO, lerp: lerpN, rad,
  // 前後に伸びた体の一直線（腕立て・プランク・斜め懸垂）を解く。
  // pivot: 支点（足首やひざ）の位置、len: 支点から肩までの長さ、sy: 肩の高さ、dirZ: 頭が +Z(1) か -Z(-1) か、face: 'down'|'up'
  line(pivot, len, sy, dirZ = 1, face = 'down') {
    const s = clamp((sy - pivot[1]) / len, -0.99, 0.99);
    const c = Math.sqrt(1 - s * s);
    const up = [0, s, c * dirZ];
    const fwd = face === 'down' ? [0, -c, s * dirZ] : [0, c, -s * dirZ];
    const sh = A.add(pivot, A.mul(up, len));
    return { up, fwd, sh, angle: Math.asin(s) * 180 / Math.PI };
  },
  // 前後の 2 つの姿勢をまぜる（バーピーのような連続動作）
  mix: (a, b, w) => ({ __mix: [a, b, w] }),
  // 周期の中の位置（t を n 回の繰り返しの何回目と、その中の 0〜1 に分ける）
  sub(t, n) { const x = t * n; const i = Math.min(n - 1, Math.floor(x)); return [i, x - i]; },
};

// ---------------------------------------------------------------- 筋肉
export const MUSCLES = {
  chest: '大胸筋', delts: '三角筋', triceps: '上腕三頭筋', biceps: '上腕二頭筋', forearms: '前腕',
  lats: '広背筋', traps: '僧帽筋', erectors: '脊柱起立筋', abs: '腹直筋', obliques: '腹斜筋',
  glutes: '大臀筋', gluteMed: '中臀筋', hipFlexors: '腸腰筋', quads: '大腿四頭筋',
  hams: 'ハムストリングス', adductors: '内転筋', calves: 'ふくらはぎ（下腿三頭筋）',
};

const COL = {
  skin: 0xdde3ea, dark: 0x262a31, prim: 0xff3d1f, sec: 0xffa624,
};

// ---------------------------------------------------------------- 形を作る
function lathe(profile, sx = 1, sz = 1, seg = 28) {
  // profile: [[y, r], ...]（y は 0 → 長さ）。両端を丸めて閉じる。
  const pts = [];
  const first = profile[0], last = profile[profile.length - 1];
  const ctrl = [[first[0] - first[1] * 0.55, 0.0001], [first[0] - first[1] * 0.35, first[1] * 0.78], ...profile,
    [last[0] + last[1] * 0.35, last[1] * 0.78], [last[0] + last[1] * 0.55, 0.0001]];
  const curve = new T.SplineCurve(ctrl.map(([y, r]) => new T.Vector2(r, y)));
  for (const p of curve.getPoints(48)) pts.push(new T.Vector2(Math.max(0.0001, p.x), p.y));
  const g = new T.LatheGeometry(pts, seg);
  g.scale(sx, 1, sz);
  g.computeVertexNormals();
  return g;
}
function ellip(rx, ry, rz, seg = 24) {
  const g = new T.SphereGeometry(1, seg, Math.round(seg * 0.75));
  g.scale(rx, ry, rz);
  return g;
}

// 部位（体の部品）。ローカル座標は Y が骨の向き、Z が前（腹・ひざ・手の甲の反対）
// 筋肉は「部位ごとの楕円の範囲」で表し、体の表面の頂点に色を塗って光らせる（境目がなめらかになる）
const REGIONS = {
  pelvis: [['glutes', [0.075, -0.05, -0.085], [0.1, 0.1, 0.085]], ['glutes', [-0.075, -0.05, -0.085], [0.1, 0.1, 0.085]],
    ['gluteMed', [0.135, 0.025, -0.02], [0.06, 0.07, 0.075]], ['gluteMed', [-0.135, 0.025, -0.02], [0.06, 0.07, 0.075]],
    ['hipFlexors', [0.07, -0.03, 0.09], [0.05, 0.065, 0.05]], ['hipFlexors', [-0.07, -0.03, 0.09], [0.05, 0.065, 0.05]]],
  abd: [['abs', [0, 0.09, 0.095], [0.075, 0.14, 0.07]], ['obliques', [0.115, 0.08, 0.02], [0.055, 0.11, 0.085]], ['obliques', [-0.115, 0.08, 0.02], [0.055, 0.11, 0.085]],
    ['erectors', [0.038, 0.09, -0.09], [0.045, 0.14, 0.05]], ['erectors', [-0.038, 0.09, -0.09], [0.045, 0.14, 0.05]]],
  rib: [['chest', [0.078, 0.185, 0.1], [0.09, 0.075, 0.07]], ['chest', [-0.078, 0.185, 0.1], [0.09, 0.075, 0.07]],
    ['abs', [0, 0.04, 0.1], [0.07, 0.08, 0.06]],
    ['obliques', [0.135, 0.04, 0.03], [0.05, 0.07, 0.085]], ['obliques', [-0.135, 0.04, 0.03], [0.05, 0.07, 0.085]],
    ['lats', [0.13, 0.12, -0.05], [0.065, 0.15, 0.085]], ['lats', [-0.13, 0.12, -0.05], [0.065, 0.15, 0.085]],
    ['traps', [0, 0.235, -0.06], [0.14, 0.085, 0.075]],
    ['erectors', [0.036, 0.06, -0.1], [0.04, 0.12, 0.05]], ['erectors', [-0.036, 0.06, -0.1], [0.04, 0.12, 0.05]]],
  neck: [['traps', [0, 0.02, -0.04], [0.085, 0.085, 0.055]]],
  upper: [['delts', [0, 0.03, 0], [0.08, 0.095, 0.08]], ['biceps', [0, 0.165, 0.04], [0.05, 0.105, 0.045]], ['triceps', [0, 0.15, -0.04], [0.055, 0.12, 0.045]]],
  fore: [['forearms', [0, 0.1, 0], [0.09, 0.17, 0.09]]],
  thigh: [['quads', [0, 0.22, 0.045], [0.085, 0.23, 0.065]], ['hams', [0, 0.21, -0.045], [0.08, 0.21, 0.06]], ['adductors', ['IN', 0.16, 0], [0.045, 0.16, 0.065]]],
  shin: [['calves', [0, 0.12, -0.04], [0.065, 0.135, 0.055]]],
};
const MIDS = Object.keys(MUSCLES);
// REGIONS は下の長さの体で作った。今の寸法に合わせて縦（Y）を伸び縮みさせる
const REF_LEN = { abd: 0.17, rib: 0.27, upper: 0.29, fore: 0.26, thigh: 0.43, shin: 0.42 };
// xz: 太さの倍率（人体モデルは簡易モデルより太いので、範囲を外側へ広げる）
function regionsFor(key, inner = 1, xz = 1) {
  const k = REF_LEN[key] ? DIM[key] / REF_LEN[key] : 1;
  return (REGIONS[key] || []).map(([id, c, r]) => [MIDS.indexOf(id), [(c[0] === 'IN' ? 0.05 * inner : c[0]) * xz, c[1] * k, c[2] * xz], [r[0] * xz, r[1] * k, r[2] * xz]]);
}
function buildBody(scene) {
  const mat = (o = {}) => new T.MeshPhysicalMaterial({ color: 0xffffff, vertexColors: true, roughness: 0.46, metalness: 0, clearcoat: 0.28, clearcoatRoughness: 0.4, sheen: 0.35, sheenRoughness: 0.6, sheenColor: new T.Color(0xffffff), ...o });
  const skinM = mat(), darkM = mat({ roughness: 0.72, clearcoat: 0.05, sheen: 0.1 });
  const SKIN = new T.Color(COL.skin), DARK = new T.Color(COL.dark), HAIR = new T.Color(0x3a3f48);
  const root = new T.Group();
  scene.add(root);
  const parts = {}, painted = [];
  const part = (name) => { const g = new T.Group(); g.userData.meshes = []; root.add(g); parts[name] = g; return g; };
  const add = (g, geo, base, pos = [0, 0, 0], rot = null) => {
    const mesh = new T.Mesh(geo, base === SKIN ? skinM : darkM);
    mesh.position.set(...pos);
    if (rot) mesh.rotation.set(...rot);
    mesh.castShadow = true; mesh.receiveShadow = true;
    mesh.userData.base = base;
    g.add(mesh); g.userData.meshes.push(mesh); return mesh;
  };

  // 骨盤（ショーツ）: 原点＝骨盤の中心
  const pel = part('pelvis');
  add(pel, ellip(0.158, 0.112, 0.104), DARK, [0, -0.03, 0]);
  add(pel, lathe([[0, 0.13], [0.06, 0.134], [0.11, 0.128]], 1.1, 0.76), DARK, [0, -0.025, 0]);
  for (const s of [1, -1]) add(pel, ellip(0.08, 0.085, 0.055), DARK, [0.066 * s, -0.05, -0.064], [0.15, 0, 0]);
  // 腹部: 原点＝骨盤の中心、上へ abd
  const abd = part('abd');
  add(abd, lathe([[0, 0.135], [0.09, 0.128], [DIM.abd, 0.137]], 1.0, 0.74), SKIN);
  // 胸郭: 原点＝腰（abd の上端）、上へ rib
  const rib = part('rib');
  add(rib, lathe([[0, 0.134], [0.1, 0.156], [0.19, 0.166], [DIM.rib - 0.01, 0.124]], 1.2, 0.7), SKIN);
  for (const s of [1, -1]) {
    add(rib, ellip(0.078, 0.06, 0.03), SKIN, [0.07 * s, 0.185, 0.088], [0, 0.18 * s, 0.12 * s]);
    add(rib, ellip(0.05, 0.12, 0.04), SKIN, [0.13 * s, 0.13, -0.035], [0, 0, -0.16 * s]);
  }
  add(rib, ellip(0.12, 0.06, 0.04), SKIN, [0, 0.24, -0.05]);
  // 首と頭: 原点＝首の付け根
  const neck = part('neck');
  add(neck, lathe([[0, 0.06], [0.1, 0.051]], 1, 0.95, 18), SKIN);
  const head = part('head');
  add(head, ellip(0.092, 0.11, 0.101), SKIN);
  add(head, ellip(0.068, 0.054, 0.074), SKIN, [0, -0.06, 0.03]);       // あご
  add(head, ellip(0.096, 0.084, 0.102), HAIR, [0, 0.036, -0.013]);   // 髪
  for (const s of [1, -1]) add(head, ellip(0.015, 0.025, 0.011), SKIN, [0.092 * s, -0.005, 0]);

  for (const s of ['L', 'R']) {
    const sg = s === 'L' ? 1 : -1;
    const up = part('upper' + s);
    add(up, lathe([[0, 0.054], [0.09, 0.051], [0.2, 0.045], [DIM.upper, 0.037]], 1, 0.95), SKIN);
    add(up, ellip(0.06, 0.075, 0.062), SKIN, [0.006 * sg, 0.035, 0]);    // 肩
    add(up, ellip(0.032, 0.08, 0.03), SKIN, [0, 0.165, 0.022]);          // 力こぶ
    add(up, ellip(0.04, 0.042, 0.04), SKIN, [0, DIM.upper, 0]);          // 肘
    const fo = part('fore' + s);
    add(fo, lathe([[0, 0.041], [0.07, 0.044], [0.18, 0.034], [DIM.fore, 0.026]], 1.08, 0.9), SKIN);
    const ha = part('hand' + s);
    add(ha, ellip(0.042, 0.06, 0.017), SKIN, [0, 0.06, 0]);
    add(ha, ellip(0.038, 0.045, 0.014), SKIN, [0, 0.12, 0.004]);
    add(ha, ellip(0.014, 0.035, 0.013), SKIN, [0.04 * sg, 0.055, 0.012], [0, 0, -0.5 * sg]); // 親指
    const th = part('thigh' + s);
    th.userData.inner = sg;
    add(th, lathe([[0, 0.095], [0.13, 0.09], [0.3, 0.071], [DIM.thigh, 0.052]], 1, 1), SKIN);
    add(th, lathe([[0, 0.097], [0.11, 0.092]], 1, 1), DARK, [0, -0.015, 0]);   // ショーツの裾
    add(th, ellip(0.06, 0.15, 0.035), SKIN, [0, 0.25, 0.036]);                 // 太ももの前
    add(th, ellip(0.052, 0.055, 0.056), SKIN, [0, DIM.thigh, 0.004]);          // ひざ
    const sh = part('shin' + s);
    add(sh, lathe([[0, 0.05], [0.1, 0.055], [0.27, 0.04], [DIM.shin, 0.031]], 1, 1), SKIN);
    add(sh, ellip(0.045, 0.1, 0.04), SKIN, [0, 0.12, -0.022]);                 // ふくらはぎ
    const ft = part('foot' + s);
    add(ft, lathe([[0, 0.042], [0.08, 0.046], [0.17, 0.04], [0.2, 0.03]], 1.12, 0.75), DARK, [0, -0.045, 0]);
  }

  // 頂点ごとに「どの筋肉にどれだけ入るか」を計算しておく
  const v = new T.Vector3();
  for (const [name, g] of Object.entries(parts)) {
    const key = name.replace(/[LR]$/, '');
    const regs = regionsFor(key, g.userData.inner || 1);
    for (const mesh of g.userData.meshes) {
      mesh.updateMatrix();
      const pos = mesh.geometry.attributes.position, n = pos.count;
      const col = new Float32Array(n * 3);
      const base = mesh.userData.base;
      for (let i = 0; i < n; i++) { col[i * 3] = base.r; col[i * 3 + 1] = base.g; col[i * 3 + 2] = base.b; }
      mesh.geometry.setAttribute('color', new T.BufferAttribute(col, 3));
      if (!regs.length) continue;
      const idx = [], wts = [];
      for (let i = 0; i < n; i++) {
        v.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrix);
        for (const [mi, c, r] of regs) {
          const d = Math.hypot((v.x - c[0]) / r[0], (v.y - c[1]) / r[1], (v.z - c[2]) / r[2]);
          if (d < 1) { idx.push(i, mi); wts.push(smooth((1 - d) / 0.45)); }
        }
      }
      if (idx.length) painted.push({ geo: mesh.geometry, col, baseArr: col.slice(), idx: Int32Array.from(idx), wts: Float32Array.from(wts) });
    }
  }
  return { root, parts, painted, tintAmt: 1, glowAmt: 0 };
}

// 筋肉の色を塗る。level[i]: 0〜1（筋肉 i の光り方）、tint[i]: 色
// tintAmt: 肌の色を筋肉の色へどれだけ寄せるか、glowAmt: 自分で光る強さ（暗い見た目のとき強く）
function paint(body, level, tint) {
  const ta = body.tintAmt, ga = body.glowAmt;
  for (const p of body.painted) {
    const { col, baseArr, idx, wts, glow } = p;
    col.set(baseArr);
    if (glow) glow.fill(0);
    for (let k = 0, j = 0; k < idx.length; k += 2, j++) {
      const mi = idx[k + 1], l = level[mi];
      if (!l) continue;
      const vi = idx[k] * 3, w = Math.min(1, wts[j]) * l, c = tint[mi], wt = w * ta;
      col[vi] += (c.r - col[vi]) * wt; col[vi + 1] += (c.g - col[vi + 1]) * wt; col[vi + 2] += (c.b - col[vi + 2]) * wt;
      if (glow) { glow[vi] += c.r * w * ga; glow[vi + 1] += c.g * w * ga; glow[vi + 2] += c.b * w * ga; }
    }
    p.geo.attributes.color.needsUpdate = true;
    if (glow) p.geo.attributes.glow.needsUpdate = true;
  }
}

// ---------------------------------------------------------------- 人体モデル（MakeHuman のメッシュを 17 の骨で動かす）
// 見た目の候補。real: 肌の質感、clay: 彫刻のような粘土、dark: 暗い体で筋肉が光る
export const STYLES = {
  real: { label: 'リアル', skin: 0xd2a07f, rough: 0.5, sheen: 0.55, sheenColor: 0xffc9a8, clear: 0.07, shorts: 0x1b1e24, hair: 0x17120f, eye: 0xf2eee8, iris: 0x2f2119, tintAmt: 0.58, glowAmt: 0.16 },
  clay: { label: 'クレイ', skin: 0xc3c7cd, rough: 0.8, sheen: 0.12, sheenColor: 0xffffff, clear: 0, shorts: 0x2b2e34, hair: 0x9a9fa6, eye: 0xc9cdd2, iris: 0xc9cdd2, tintAmt: 0.78, glowAmt: 0.1 },
  dark: { label: 'ダーク', skin: 0x2e2f33, rough: 0.4, sheen: 0.45, sheenColor: 0xd9dce2, clear: 0.5, shorts: 0x0c0c0d, hair: 0x141416, eye: 0x2e2f33, iris: 0x2e2f33, tintAmt: 0.42, glowAmt: 1.15 }, // 黒鉛色の体（黒と白のサイトに合わせる）
};

// 筋肉の表示（人体モデル用）: 頂点の mus 属性（番号・形の重み・線維を横切る位置・腱）から、
// 効いている筋肉を筋線維の縞と凹凸つきで描き、力を出すほど少し張らせて光らせる。強さは uniform で毎フレーム渡す。
const MUSCLE_GLSL = {
  vertexHead: `attribute vec4 mus;
uniform float uLevel[17];
uniform vec3 uTint[17];
uniform float uBulge;
varying vec4 vMusC;
varying vec3 vMus;`,
  vertexBegin: `vMus = mus.yzw; vMusC = vec4(0.0);
if (mus.x < 16.5) {
  int mi = int(mus.x + 0.5);
  vMusC = vec4(uTint[mi], uLevel[mi]);
  transformed += musN0 * uBulge * mus.y * uLevel[mi] * (1.0 - mus.w);
}`,
  fragHead: `uniform float uLevel[17];
uniform vec3 uTint[17];
uniform float uGlow, uTintAmt, uBump, uCloth;
uniform vec3 uSheen;
varying vec4 vMusC;
varying vec3 vMus;
float musFiber(float u) {
  // 線維の束（太い縞）と細い線維。画面上で細かすぎる縞は消す（ちらつき防止）
  float fw = fwidth(u);
  float coarse = 0.5 + 0.5 * cos(6.2832 * u / 0.011);
  float fine = 0.5 + 0.5 * cos(6.2832 * u / 0.0042 + 2.1 * coarse);
  coarse = mix(0.5, coarse, 1.0 - smoothstep(0.003, 0.007, fw));
  fine = mix(0.5, fine, 1.0 - smoothstep(0.0012, 0.0026, fw));
  return clamp(coarse * 0.62 + fine * 0.38, 0.0, 1.0);
}`,
  fragColor: `float musL = vMusC.a; vec3 musTint = vMusC.rgb / max(1e-3, max(vMusC.r, max(vMusC.g, vMusC.b))) * max(vMusC.r, max(vMusC.g, vMusC.b));
float musW = vMus.x, musT = vMus.z;
float musF = musW > 0.0 ? musFiber(vMus.y) : 0.5;
float musA = musW * musL * (1.0 - uCloth * 0.72); // 布の上からは控えめに透けて見せる
vec3 musCol = mix(musTint * 0.58, musTint * 1.06, smoothstep(0.1, 0.95, musF));
musCol = mix(musCol, vec3(0.93, 0.89, 0.83), musT);
diffuseColor.rgb = mix(diffuseColor.rgb, musCol, clamp(musA * uTintAmt * (1.0 - uCloth * 0.4), 0.0, 1.0));
vec3 musEmit = musCol * musA * uGlow * (0.62 + 0.38 * musF) * (1.0 - 0.75 * musT) * (1.0 - uCloth * 0.5);`,
  fragNormal: `{
  // 筋線維の凹凸（高さ h をもとに法線を傾ける）
  float h = (musF - 0.5) * vMus.x * (1.0 - musT) * uBump * musL * (1.0 - uCloth);
  vec3 dpx = dFdx(-vViewPosition), dpy = dFdy(-vViewPosition);
  float hx = dFdx(h), hy = dFdy(h);
  vec3 r1 = cross(dpy, normal), r2 = cross(normal, dpx);
  float det = dot(dpx, r1);
  vec3 g = sign(det) * (hx * r1 + hy * r2);
  normal = normalize(abs(det) * normal - g);
}`,
};
// 布（ショーツ）: ウエストのゴムの畝・縫い目・裾の折り返し。裏側は暗く
const CLOTH_GLSL = {
  vertexHead: 'attribute vec3 cloth;\nvarying vec3 vCloth;',
  vertexBegin: 'vCloth = cloth;',
  fragHead: 'varying vec3 vCloth;',
  fragColor: `{
  float hemD = vCloth.x, wD = vCloth.y, th = vCloth.z;
  float aa = fwidth(th);
  // 縫い目: 裾から 2cm・ウエストのゴムの下（4.2cm）。点線
  float dash = smoothstep(0.35, 0.5, fract(th * 0.55)) * (1.0 - smoothstep(0.85, 1.0, fract(th * 0.55)));
  float hemW = fwidth(hemD), wW = fwidth(wD);
  float stitch = (1.0 - smoothstep(0.0005, 0.0005 + hemW * 1.5, abs(hemD - 0.021))) + (1.0 - smoothstep(0.0005, 0.0005 + wW * 1.5, abs(wD - 0.042)));
  stitch *= dash * (1.0 - smoothstep(0.6, 1.2, aa));
  // ウエストのゴム（縦の畝）と裾の折り返しは少し明るく
  float band = 1.0 - smoothstep(0.036, 0.04, wD);
  float rib = band * (0.5 + 0.5 * cos(th * 6.2832 / 1.6)) * (1.0 - smoothstep(0.3, 0.7, aa));
  float fold = 1.0 - smoothstep(0.024, 0.027, hemD);
  vec3 c = diffuseColor.rgb * (1.0 + 0.18 * band + 0.1 * fold - 0.12 * rib);
  c = mix(c, vec3(0.42), clamp(stitch, 0.0, 1.0) * 0.55);
  if (!gl_FrontFacing) c *= 0.35;
  diffuseColor.rgb = c;
}`,
};
// 皮膚の変形: 双対クォータニオン（DQS）。線形の混ぜ方（three.js の標準）は、肘・膝を深く曲げると
// 内側がつぶれ、ねじると細くなる（キャンディの包み紙のようになる）。DQS は体積を保ったまま曲がる。
const DQ_LIN = (typeof window !== 'undefined' && window.__DQLIN != null) ? window.__DQLIN : 0.85; // 肩のまわりで線形を混ぜる割合
const DQ_GLSL = {
  head: `attribute float dqm;
uniform vec4 uDQr[NBONES];
uniform vec4 uDQd[NBONES];
vec4 dqR, dqD;
void dqAdd(float w, int i) {
  vec4 r = uDQr[i];
  float sg = dot(dqR, r) < 0.0 ? -1.0 : 1.0;
  dqR += w * sg * r; dqD += w * sg * uDQd[i];
}
void dqBlend() {
  ivec4 id = ivec4(skinIndex + 0.5);
  dqR = skinWeight.x * uDQr[id.x]; dqD = skinWeight.x * uDQd[id.x];
  if (skinWeight.y > 0.0) dqAdd(skinWeight.y, id.y);
  if (skinWeight.z > 0.0) dqAdd(skinWeight.z, id.z);
  if (skinWeight.w > 0.0) dqAdd(skinWeight.w, id.w);
  float l = length(dqR); dqR /= l; dqD /= l;
}
vec3 dqDir(vec3 v) { return v + 2.0 * cross(dqR.xyz, cross(dqR.xyz, v) + dqR.w * v); }
vec3 dqPos(vec3 p) { return dqDir(p) + 2.0 * (dqR.w * dqD.xyz - dqD.w * dqR.xyz + cross(dqR.xyz, dqD.xyz)); }`,
  // 肩のまわり（胸・鎖骨・上腕の付け根がまざる所）だけは、線形の変形と半々にまぜる（attribute dqm。1=DQS だけ）。
  // 腕を真上まで上げると回転が 150 度を超え、DQS だけでは肩がふくらむため（線形はしぼむので、まぜると打ち消し合う）
  base: '#ifdef USE_SKINNING\n#include <skinbase_vertex>\ndqBlend();\n#endif',
  normal: '#ifdef USE_SKINNING\nmat4 skinMatrix = mat4( 0.0 );\nskinMatrix += skinWeight.x * boneMatX; skinMatrix += skinWeight.y * boneMatY; skinMatrix += skinWeight.z * boneMatZ; skinMatrix += skinWeight.w * boneMatW;\nskinMatrix = bindMatrixInverse * skinMatrix * bindMatrix;\nobjectNormal = normalize(mix(vec4( skinMatrix * vec4( objectNormal, 0.0 ) ).xyz, dqDir(objectNormal), dqm));\n#ifdef USE_TANGENT\nobjectTangent = dqDir(objectTangent);\n#endif\n#endif',
  pos: '#ifdef USE_SKINNING\nvec4 skinVertex = bindMatrix * vec4( transformed, 1.0 );\nvec4 skinned = vec4( 0.0 );\nskinned += boneMatX * skinVertex * skinWeight.x; skinned += boneMatY * skinVertex * skinWeight.y; skinned += boneMatZ * skinVertex * skinWeight.z; skinned += boneMatW * skinVertex * skinWeight.w;\ntransformed = mix(( bindMatrixInverse * skinned ).xyz, dqPos(transformed), dqm);\n#endif',
};
function dqShader(sh, U) {
  const nb = U.uDQr.value.length;
  sh.uniforms.uDQr = U.uDQr; sh.uniforms.uDQd = U.uDQd;
  sh.vertexShader = sh.vertexShader.replace('#include <common>', '#include <common>\n' + DQ_GLSL.head.replaceAll('NBONES', String(nb)))
    .replace('#include <skinbase_vertex>', DQ_GLSL.base)
    .replace('#include <skinnormal_vertex>', DQ_GLSL.normal)
    .replace('#include <skinning_vertex>', DQ_GLSL.pos);
}
// 影を描くときも同じ変形にする
function withDQ(mat, U) {
  mat.onBeforeCompile = (sh) => dqShader(sh, U);
  mat.customProgramCacheKey = () => 'jiju-dq';
  return mat;
}

function withMuscles(mat, U) {
  const cloth = !!U.uCloth.value;
  mat.onBeforeCompile = (sh) => {
    Object.assign(sh.uniforms, U);
    if (U.uDQr) dqShader(sh, U);
    sh.vertexShader = sh.vertexShader.replace('#include <common>', '#include <common>\n' + MUSCLE_GLSL.vertexHead + (cloth ? '\n' + CLOTH_GLSL.vertexHead : ''))
      .replace('#include <beginnormal_vertex>', '#include <beginnormal_vertex>\nvec3 musN0 = objectNormal;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n' + MUSCLE_GLSL.vertexBegin + (cloth ? '\n' + CLOTH_GLSL.vertexBegin : ''));
    sh.fragmentShader = sh.fragmentShader.replace('#include <common>', '#include <common>\n' + MUSCLE_GLSL.fragHead + (cloth ? '\n' + CLOTH_GLSL.fragHead : ''))
      .replace('#include <color_fragment>', '#include <color_fragment>\n' + (cloth ? CLOTH_GLSL.fragColor + '\n' : '') + MUSCLE_GLSL.fragColor)
      .replace('#include <normal_fragment_maps>', '#include <normal_fragment_maps>\n' + MUSCLE_GLSL.fragNormal)
      .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\ntotalEmissiveRadiance += musEmit;')
      .replace('#include <lights_fragment_end>', '#include <lights_fragment_end>\n{ float fr = 1.0 - saturate(dot(normal, geometryViewDir)); reflectedLight.indirectSpecular += uSheen * (fr * fr * fr) * (0.35 + 0.65 * saturate(normal.y * 0.5 + 0.5)); }');
  };
  mat.customProgramCacheKey = () => 'jiju-muscles-' + (U.uCloth.value ? 'c' : 's') + (U.uDQr ? '-dq' : '');
  return mat;
}

const GEO = {};
function buildSkinned(scene, B, st) {
  const geo = GEO[st.label] || (GEO[st.label] = skinnedGeo(B, st));
  return skinnedMesh(scene, B, st, geo);
}
function skinnedGeo(B, st) {
  const dec = (b64, Type) => { const bin = atob(b64); const u8 = new Uint8Array(bin.length); for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i); return new Type(u8.buffer); };
  const n = B.COUNT, q = dec(B.POS, Uint16Array), [mn, mx] = B.BOX;
  const pos = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) for (let a = 0; a < 3; a++) pos[i * 3 + a] = mn[a] + (q[i * 3 + a] / 65535) * (mx[a] - mn[a]);
  const index = dec(B.INDEX, Uint16Array), si = dec(B.SKIN_I, Uint8Array), sw = dec(B.SKIN_W, Uint8Array);
  const geo = new T.BufferGeometry();
  geo.setAttribute('position', new T.BufferAttribute(pos, 3));
  geo.setIndex(new T.BufferAttribute(index, 1));
  geo.computeVertexNormals();
  // 髪は体から少し浮かせる（重なってちらつかないように）
  const vertsOf = (name) => { if (!B.RANGES[name]) return new Set(); const [o, c] = B.RANGES[name]; return new Set(index.subarray(o, o + c)); };
  const nrm = geo.attributes.normal.array;
  for (const [name, off] of [['hair', 0.0035]]) for (const i of vertsOf(name)) for (let a = 0; a < 3; a++) pos[i * 3 + a] += nrm[i * 3 + a] * off;
  geo.computeVertexNormals();
  geo.setAttribute('skinIndex', new T.Uint16BufferAttribute(Uint16Array.from(si), 4));
  geo.setAttribute('skinWeight', new T.Float32BufferAttribute(Float32Array.from(sw, (x) => x / 255), 4));
  const groups = ['body', 'shorts', 'hair', 'eyes']; // 材料の番号: 肌・布・髪・目
  groups.forEach((g, k) => { if (B.RANGES[g]) { const [o, c] = B.RANGES[g]; geo.addGroup(o, c, k); } });

  // 頂点の色（もとの色）
  const baseArr = new Float32Array(n * 3);
  const setCol = (set, hex) => { const c = new T.Color(hex); for (const i of set) { baseArr[i * 3] = c.r; baseArr[i * 3 + 1] = c.g; baseArr[i * 3 + 2] = c.b; } };
  setCol(vertsOf('body'), st.skin); setCol(vertsOf('shorts'), st.shorts); setCol(vertsOf('hair'), st.hair);
  // ショーツの下の皮膚も同じ色にしておく（動いたときに布のすき間から肌が見えないように）。布そのものは shorts の頂点（scripts/jiju/shorts.mjs）
  const shortsF = new Float32Array(n);
  {
    const ix = (nm) => B.PARTS.indexOf(nm);
    const binv = B.PARTS.map((nm) => { const b = B.BIND[nm]; return new T.Matrix4().compose(new T.Vector3(...b.o), new T.Quaternion(...b.q), new T.Vector3(1, 1, 1)).invert(); });
    const thighs = [ix('thighL'), ix('thighR')], below = new Set(['shinL', 'shinR', 'footL', 'footR'].map(ix));
    const arms = new Set(['upperL', 'ushL', 'foreL', 'twistL', 'handL', 'fingL', 'tipL', 'thumbL', 'upperR', 'ushR', 'foreR', 'twistR', 'handR', 'fingR', 'tipR', 'thumbR'].map(ix));
    const shortsC = new T.Color(st.shorts), v3 = new T.Vector3();
    const hem = DIM.thigh * 0.44, waistY = 0.075;
    for (const i of vertsOf('body')) {
      v3.set(pos[i * 3], pos[i * 3 + 1], pos[i * 3 + 2]).applyMatrix4(binv[0]);
      const fw = 1 - smooth((v3.y - waistY) / 0.012 + 0.5);
      if (fw <= 0) continue;
      let beyond = 0, arm = 0;
      for (let k = 0; k < 4; k++) {
        const b = si[i * 4 + k], w = sw[i * 4 + k] / 255;
        if (!w) continue;
        if (thighs.includes(b)) { v3.set(pos[i * 3], pos[i * 3 + 1], pos[i * 3 + 2]).applyMatrix4(binv[b]); beyond += w * smooth((v3.y - hem) / 0.008 + 0.5); }
        else if (below.has(b)) beyond += w;
        else if (arms.has(b)) arm += w;
      }
      const f = fw * (1 - smooth((beyond - 0.5) / 0.2 + 0.5)) * (1 - arm);
      if (f <= 0) continue;
      shortsF[i] = f;
      for (let a = 0; a < 3; a++) baseArr[i * 3 + a] = lerpN(baseArr[i * 3 + a], [shortsC.r, shortsC.g, shortsC.b][a], f);
    }
  }
  // 布の属性（裾・ウエストからの距離 m、体のまわりの角度 度）。体の頂点は 0
  const clothA = new Float32Array(n * 3);
  if (B.CLOTH) {
    const c = dec(B.CLOTH, Int16Array);
    for (let k = 0; k < c.length / 3; k++) { const i = B.SHORTS_START + k; clothA[i * 3] = c[k * 3] / 10000; clothA[i * 3 + 1] = c[k * 3 + 1] / 10000; clothA[i * 3 + 2] = c[k * 3 + 2] / 100; }
  }
  geo.setAttribute('cloth', new T.BufferAttribute(clothA, 3));
  // 短い髪: 頭の骨から見て、生え際より上の頭皮に髪の色を塗る（前は額の上、横は耳の上、後ろはうなじまで）
  {
    const headI = B.PARTS.indexOf('head'), hb = B.BIND.head;
    const hinv = new T.Matrix4().compose(new T.Vector3(...hb.o), new T.Quaternion(...hb.q), new T.Vector3(1, 1, 1)).invert();
    const skinC = new T.Color(st.skin), hairC = new T.Color(st.hair), v2 = new T.Vector3();
    for (const i of vertsOf('body')) {
      if (si[i * 4] !== headI) continue;
      v2.set(pos[i * 3], pos[i * 3 + 1], pos[i * 3 + 2]).applyMatrix4(hinv);
      const f = clamp((v2.z / 0.1 + 1) / 2, 0, 1);              // 0=後ろ, 1=前
      const line = lerpN(-0.045, 0.072, f * f) + (Math.abs(v2.x) > 0.06 && f > 0.25 && f < 0.8 ? 0.03 : 0);
      const h = smooth((v2.y - line) / 0.014 + 0.5);
      if (h <= 0) continue;
      baseArr[i * 3] = lerpN(skinC.r, hairC.r, h); baseArr[i * 3 + 1] = lerpN(skinC.g, hairC.g, h); baseArr[i * 3 + 2] = lerpN(skinC.b, hairC.b, h);
    }
  }
  // 目: 正面を向いた部分を黒目にする
  const eyes = [...vertsOf('eyes')];
  for (const side of [1, -1]) {
    const ids = eyes.filter((i) => Math.sign(pos[i * 3]) === side);
    const c = [0, 0, 0]; for (const i of ids) for (let a = 0; a < 3; a++) c[a] += pos[i * 3 + a] / ids.length;
    const white = new T.Color(st.eye), iris = new T.Color(st.iris), pupil = new T.Color(0x0b0b0c);
    for (const i of ids) {
      const d = new T.Vector3(pos[i * 3] - c[0], pos[i * 3 + 1] - c[1], pos[i * 3 + 2] - c[2]).normalize();
      const col = d.z > 0.95 ? pupil : d.z > 0.8 ? iris : white;
      baseArr[i * 3] = col.r; baseArr[i * 3 + 1] = col.g; baseArr[i * 3 + 2] = col.b;
    }
  }
  const col = baseArr.slice();
  geo.setAttribute('color', new T.BufferAttribute(col, 3));
  // 筋肉の属性（番号・形の重み・線維を横切る位置 m・腱）
  const mid = dec(B.MUS_ID, Uint8Array), mw = dec(B.MUS_W, Uint8Array), mu = dec(B.MUS_U, Int16Array), mt = dec(B.MUS_T, Uint8Array);
  const musA = new Float32Array(n * 4);
  for (let i = 0; i < n; i++) { musA[i * 4] = mid[i] === 255 ? 99 : mid[i]; musA[i * 4 + 1] = mw[i] / 255; musA[i * 4 + 2] = mu[i] / 10000; musA[i * 4 + 3] = mt[i] / 255; }
  geo.setAttribute('mus', new T.BufferAttribute(musA, 4));
  // DQS と線形の混ぜ具合（肩のまわりだけ線形を混ぜる。DQ_GLSL の説明を参照）
  const dqmA = new Float32Array(n).fill(1);
  const shoulderIds = new Set(['clavL', 'clavR', 'ushL', 'ushR'].map((nm) => B.PARTS.indexOf(nm)).filter((k) => k >= 0));
  for (let i = 0; i < n; i++) {
    let w = 0;
    for (let k = 0; k < 4; k++) if (shoulderIds.has(si[i * 4 + k])) w += sw[i * 4 + k] / 255;
    dqmA[i] = 1 - DQ_LIN * Math.min(1, 4 * w * (1 - w) * 1.4);
  }
  geo.setAttribute('dqm', new T.BufferAttribute(dqmA, 1));
  return geo;
}
function skinnedMesh(scene, B, st, geo) {
  const NB = B.PARTS.length;
  const dqU = { uDQr: { value: Array.from({ length: NB }, () => new T.Vector4(0, 0, 0, 1)) }, uDQd: { value: Array.from({ length: NB }, () => new T.Vector4()) } };
  const shared = { ...dqU, uLevel: { value: new Array(17).fill(0) }, uTint: { value: Array.from({ length: 17 }, () => new T.Color()) }, uGlow: { value: st.glowAmt }, uTintAmt: { value: st.tintAmt }, uBump: { value: 0.0016 }, uBulge: { value: 0.003 } };
  // 軽い材料（MeshStandardMaterial）。光沢の縁（sheen）は、視線と面の角度から足す安い近似で出す
  const sheenOf = (hex, amt) => new T.Color(hex).multiplyScalar(amt);
  const mat = (o, cloth, sheen) => withMuscles(new T.MeshStandardMaterial({ color: 0xffffff, vertexColors: true, metalness: 0, ...o }), { ...shared, uCloth: { value: cloth }, uSheen: { value: sheen } });
  const mats = [
    mat({ roughness: st.rough * (1 - 0.3 * st.clear) }, 0, sheenOf(st.sheenColor, st.sheen * 0.7)),
    mat({ roughness: 0.82, side: T.DoubleSide }, 1, sheenOf(0x8a93a0, 0.45)),
    mat({ roughness: 0.95 }, 1, sheenOf(0x6a6058, 0.1)),
    new T.MeshStandardMaterial({ color: 0xffffff, vertexColors: true, roughness: 0.12 }),
  ];

  // 骨（階層なし。jiju-3d の rig が世界座標の位置と向きを直接入れる）
  const root = new T.Group(); scene.add(root);
  const parts = {}, bones = [], inv = [];
  for (const name of B.PARTS) {
    const bone = new T.Bone(); root.add(bone); parts[name] = bone; bones.push(bone);
    const { o, q: bq } = B.BIND[name];
    inv.push(new T.Matrix4().compose(new T.Vector3(...o), new T.Quaternion(...bq), new T.Vector3(1, 1, 1)).invert());
  }
  const mesh = new T.SkinnedMesh(geo, mats);
  mesh.castShadow = true; mesh.receiveShadow = true; mesh.frustumCulled = false;
  mesh.customDepthMaterial = withDQ(new T.MeshDepthMaterial({ depthPacking: T.RGBADepthPacking }), dqU);
  root.add(mesh);
  mesh.bind(new T.Skeleton(bones, inv), new T.Matrix4());

  // 骨ごとの変形（基準の姿勢 → いまの姿勢）を双対クォータニオンにする。rig が骨を置いたあとに呼ぶ
  const bindQ = B.PARTS.map((nm) => new T.Quaternion(...B.BIND[nm].q).invert());
  const bindO = B.PARTS.map((nm) => new T.Vector3(...B.BIND[nm].o));
  const qr = new T.Quaternion(), tv = new T.Vector3();
  const syncDQ = () => {
    for (let k = 0; k < NB; k++) {
      const bone = bones[k];
      qr.multiplyQuaternions(bone.quaternion, bindQ[k]);
      tv.copy(bindO[k]).applyQuaternion(qr).negate().add(bone.position);
      const r = dqU.uDQr.value[k], d = dqU.uDQd.value[k];
      r.set(qr.x, qr.y, qr.z, qr.w);
      // 平行移動 t の双対部: 0.5 * (t, 0) * q
      d.set(
        0.5 * (tv.x * qr.w + tv.y * qr.z - tv.z * qr.y),
        0.5 * (-tv.x * qr.z + tv.y * qr.w + tv.z * qr.x),
        0.5 * (tv.x * qr.y - tv.y * qr.x + tv.z * qr.w),
        0.5 * (-tv.x * qr.x - tv.y * qr.y - tv.z * qr.z));
    }
  };
  // 筋肉の光り方（jiju-3d の frame() が毎フレーム呼ぶ）
  const setLevels = (level, tint) => {
    for (let k = 0; k < 17; k++) { shared.uLevel.value[k] = level[k] || 0; shared.uTint.value[k].copy(tint[k]); }
  };
  // 足の指（足から見た向き）と親指（手から見た向きと付け根の位置）。立った姿勢のときの値
  const toeRest = {}, thumbRest = {};
  const qOf = (nm) => new T.Quaternion(...B.BIND[nm].q);
  for (const s of ['L', 'R']) {
    if (B.BIND['toes' + s]) toeRest[s] = new T.Vector3(0, 1, 0).applyQuaternion(qOf('toes' + s)).applyQuaternion(qOf('foot' + s).invert()).toArray();
    if (B.BIND['thumb' + s]) {
      const hq = qOf('hand' + s).invert();
      thumbRest[s] = {
        dir: new T.Vector3(0, 1, 0).applyQuaternion(qOf('thumb' + s)).applyQuaternion(hq).toArray(),
        off: new T.Vector3(...B.BIND['thumb' + s].o).sub(new T.Vector3(...B.BIND['hand' + s].o)).applyQuaternion(hq).toArray(),
      };
    }
  }
  return { root, parts, painted: [], setLevels, syncDQ, dq: dqU, bind: B.BIND, skinned: true, toeRest, thumbRest };
}

// ---------------------------------------------------------------- 姿勢を解く
function makeSolver(body = {}) {
  const V = (a) => new T.Vector3(a[0], a[1], a[2]);
  const q = new T.Quaternion(), e = new T.Euler(), m = new T.Matrix4();
  const tmp = new T.Vector3(), tmp2 = new T.Vector3();

  const quatFromAxes = (up, fwd) => {
    const y = V(up).normalize();
    const z = V(fwd); z.addScaledVector(y, -z.dot(y)).normalize();
    const x = new T.Vector3().crossVectors(y, z);
    m.makeBasis(x, y, z);
    return new T.Quaternion().setFromRotationMatrix(m);
  };
  const eq = (ang) => { // [曲げ(前), 横, ねじり] 度
    if (!ang) return new T.Quaternion();
    e.set(rad(ang[0] || 0), rad(ang[2] || 0), rad(ang[1] || 0), 'XYZ');
    return new T.Quaternion().setFromEuler(e);
  };
  const ik2 = (S, Tg, pole, a, b) => {
    const d = tmp.subVectors(Tg, S);
    let L = d.length();
    const n = d.clone().normalize();
    const maxL = (a + b) * 0.9995, minL = Math.abs(a - b) + 0.01;
    L = clamp(L, minL, maxL);
    const end = S.clone().addScaledVector(n, L);
    const cosA = clamp((a * a + L * L - b * b) / (2 * a * L), -1, 1);
    const sinA = Math.sqrt(1 - cosA * cosA);
    const mm = pole.clone().addScaledVector(n, -pole.dot(n));
    if (mm.lengthSq() < 1e-8) mm.set(n.y, -n.x, 0.3).addScaledVector(n, -0.3 * n.z);
    mm.normalize();
    const E = S.clone().addScaledVector(n, a * cosA).addScaledVector(mm, a * sinA);
    return { E, W: end };
  };

  // 姿勢の定義 → 世界座標の関節（まぜる前に一度すべて世界座標にする）
  function resolve(spec) {
    if (spec.__mix) {
      const [a, b, w] = spec.__mix;
      return blend(resolve(a), resolve(b), w);
    }
    const D = DIM;
    const r = { props: spec.props || null };
    r.P0 = V(spec.root.p);
    r.Q0 = spec.root.q ? spec.root.q.clone() : quatFromAxes(spec.root.up || [0, 1, 0], spec.root.fwd || [0, 0, 1]);
    r.lumbar = spec.lumbar || [0, 0, 0];
    r.thoracic = spec.thoracic || [0, 0, 0];
    r.neck = spec.neck || [0, 0, 0];
    frames(r);
    const res = (t) => {
      if (!t) return null;
      if (Array.isArray(t)) return V(t);
      const v = V(t.v);
      switch (t.f) {
        case 'pelvis': return v.applyQuaternion(r.Q0).add(r.P0);
        case 'rib': return v.applyQuaternion(r.Qr).add(r.W);
        case 'head': return v.applyQuaternion(r.Qh).add(r.H);
        case 'shL': return v.add(r.shL); case 'shR': return v.add(r.shR);
        case 'hipL': return v.add(r.hipL); case 'hipR': return v.add(r.hipR);
        default: return v;
      }
    };
    const resDir = (t, fallback) => {
      if (!t) return fallback.clone();
      if (Array.isArray(t)) return V(t).normalize();
      const v = V(t.v);
      if (t.f === 'pelvis') v.applyQuaternion(r.Q0);
      else if (t.f === 'rib') v.applyQuaternion(r.Qr);
      else if (t.f === 'head') v.applyQuaternion(r.Qh);
      return v.normalize();
    };
    for (const s of ['L', 'R']) {
      const arm = spec['arm' + s] || {};
      const leg = spec['leg' + s] || {};
      const sg = s === 'L' ? 1 : -1;
      const sideW = new T.Vector3(sg, 0, 0).applyQuaternion(r.Qr);
      const downW = new T.Vector3(0, -1, 0).applyQuaternion(r.Qr);
      r['wristT' + s] = res(arm.to) || r['sh' + s].clone().addScaledVector(downW, 0.5).addScaledVector(sideW, 0.08);
      // 手の目標が体についているか（腕組み・腰に手など）。体を下げるときに一緒に動かす
      r['wristRel' + s] = !arm.to || (!Array.isArray(arm.to) && !!arm.to.f);
      r['elbowP' + s] = resDir(arm.pole, new T.Vector3(0.3 * sg, 0, -1).applyQuaternion(r.Qr));
      r['handDir' + s] = arm.hand && arm.hand.dir ? resDir(arm.hand.dir) : null;
      r['handUp' + s] = arm.hand && arm.hand.up ? resDir(arm.hand.up) : null;
      r['curl' + s] = arm.hand && arm.hand.curl != null ? arm.hand.curl : null;
      r['scap' + s] = arm.scap != null ? arm.scap : null;
      const pdown = new T.Vector3(0, -1, 0).applyQuaternion(r.Q0);
      r['ankleT' + s] = res(leg.to) || r['hip' + s].clone().addScaledVector(pdown, D.leg * 0.99);
      r['kneeP' + s] = resDir(leg.pole, new T.Vector3(0.1 * sg, 0, 1).applyQuaternion(r.Q0));
      r['toeT' + s] = res(leg.toe);
    }
    return r;
  }

  function frames(r) {
    const D = DIM;
    r.Qa = r.Q0.clone().multiply(eq(r.lumbar));
    r.W = new T.Vector3(0, D.abd, 0).applyQuaternion(r.Qa).add(r.P0);
    r.Qr = r.Qa.clone().multiply(eq(r.thoracic));
    r.C = new T.Vector3(0, D.rib, 0).applyQuaternion(r.Qr).add(r.W);
    r.shL = new T.Vector3(D.shX, D.shY, D.shZ).applyQuaternion(r.Qr).add(r.C);
    r.shR = new T.Vector3(-D.shX, D.shY, D.shZ).applyQuaternion(r.Qr).add(r.C);
    r.clvL = new T.Vector3(D.clvX || 0.02, D.clvY || -0.07, D.clvZ || 0.02).applyQuaternion(r.Qr).add(r.C);
    r.clvR = new T.Vector3(-(D.clvX || 0.02), D.clvY || -0.07, D.clvZ || 0.02).applyQuaternion(r.Qr).add(r.C);
    r.Qh = r.Qr.clone().multiply(eq(r.neck));
    r.N = r.C.clone();
    r.H = new T.Vector3(0, D.headY, D.headZ).applyQuaternion(r.Qh).add(r.N);
    r.hipL = new T.Vector3(D.hipX, -D.hipDrop, D.hipZ).applyQuaternion(r.Q0).add(r.P0);
    r.hipR = new T.Vector3(-D.hipX, -D.hipDrop, D.hipZ).applyQuaternion(r.Q0).add(r.P0);
  }

  function blend(a, b, w) {
    const r = { props: w < 0.5 ? a.props : b.props };
    r.P0 = a.P0.clone().lerp(b.P0, w);
    r.Q0 = a.Q0.clone().slerp(b.Q0, w);
    r.lumbar = a.lumbar.map((x, i) => lerpN(x, b.lumbar[i], w));
    r.thoracic = a.thoracic.map((x, i) => lerpN(x, b.thoracic[i], w));
    r.neck = a.neck.map((x, i) => lerpN(x, b.neck[i], w));
    frames(r);
    for (const s of ['L', 'R']) {
      for (const k of ['wristT', 'ankleT']) r[k + s] = a[k + s].clone().lerp(b[k + s], w);
      r['wristRel' + s] = w < 0.5 ? a['wristRel' + s] : b['wristRel' + s];
      const sa = a['scap' + s] != null ? a['scap' + s] : 1, sb = b['scap' + s] != null ? b['scap' + s] : 1;
      r['scap' + s] = lerpN(sa, sb, w);
      for (const k of ['elbowP', 'kneeP']) r[k + s] = a[k + s].clone().lerp(b[k + s], w).normalize();
      const ca = a['curl' + s], cb = b['curl' + s];
      r['curl' + s] = ca != null && cb != null ? lerpN(ca, cb, w) : (w < 0.5 ? ca : cb);
      for (const k of ['handDir', 'handUp', 'toeT']) {
        const x = a[k + s], y = b[k + s];
        r[k + s] = x && y ? x.clone().lerp(y, w) : (w < 0.5 ? x : y);
        if (r[k + s] && k !== 'toeT') r[k + s] = r[k + s].clone().normalize();
      }
    }
    return r;
  }

  // 肩甲上腕リズム: 腕を上げると、鎖骨と肩甲骨も一緒に回って肩関節が上がる。
  // 最初の 30 度はほぼ肩関節だけ、その先は腕 2 : 肩甲骨 1（2:1 の比）。鎖骨の外側は最大およそ 40 度上がる。
  // 腕を前へ出すときは、肩甲骨が少し前へ回る（前方突出）
  const yAxis = new T.Vector3(0, 1, 0), zAxis = new T.Vector3(0, 0, 1);
  function shoulderRhythm(r, s) {
    const sg = s === 'L' ? 1 : -1;
    const S = r['sh' + s], Cl = r['clv' + s], Wt = r['wristT' + s];
    const d = Wt.clone().sub(S); const L = d.length();
    if (L < 1e-4) return;
    d.divideScalar(L);
    const ribUp = yAxis.clone().applyQuaternion(r.Qr), ribFwd = zAxis.clone().applyQuaternion(r.Qr);
    const elev = Math.acos(clamp(-d.dot(ribUp), -1, 1)) * 180 / Math.PI; // 0 = 体の横に下ろす・180 = 真上
    const scap = r['scap' + s] != null ? r['scap' + s] : 1; // 種目ごとの強さ（懸垂の引き上げでは肩甲骨を下げるので小さく）
    const lift = clamp((elev - 30) * 0.4, 0, 55) * scap; // 肩甲骨の上方回旋は腕を上げきると約 60 度
    const prot = clamp(d.dot(ribFwd), 0, 1) * 12 * smooth(elev / 80) * scap;
    if (lift < 0.01 && prot < 0.01) return;
    const q = new T.Quaternion().setFromAxisAngle(ribFwd, rad(sg * lift))
      .multiply(new T.Quaternion().setFromAxisAngle(ribUp, rad(-sg * prot)));
    r['sh' + s] = S.clone().sub(Cl).applyQuaternion(q).add(Cl);
  }

  // 手足を IK で解いて、関節の位置を全部そろえる
  function limbs(r) {
    const D = DIM;
    // 床に立つ足に脚が届かないとき（足幅が広いのに腰が高いなど）は、足を浮かせずに体を下げる。
    // 以前は足が床から最大 3cm 浮き、立つたびに小さく跳ねて見えた
    let drop = 0;
    const reach = (D.thigh + D.shin) * 0.997;
    for (const s of ['L', 'R']) {
      const A = r['ankleT' + s];
      if (!A || A.y > 0.16) continue;
      const h = r['hip' + s];
      const dx = h.x - A.x, dz = h.z - A.z, dy = h.y - A.y;
      const flat = dx * dx + dz * dz;
      if (flat >= reach * reach || dx * dx + dy * dy + dz * dz <= reach * reach) continue;
      // 足を上げていく途中（足首 10〜16cm）は少しずつ効き目を弱め、急に体が上下しないようにする
      const on = smooth((0.16 - A.y) / 0.06);
      drop = Math.max(drop, (dy - Math.sqrt(reach * reach - flat)) * on);
    }
    if (drop > 0) {
      r.P0.y -= drop; frames(r);
      for (const s of ['L', 'R']) if (r['wristRel' + s]) r['wristT' + s].y -= drop;
    }
    for (const s of ['L', 'R']) {
      shoulderRhythm(r, s);
      const arm = ik2(r['sh' + s], r['wristT' + s], r['elbowP' + s], D.upper, D.fore);
      r['elbow' + s] = arm.E; r['wrist' + s] = arm.W;
      const fdir = arm.W.clone().sub(arm.E).normalize();
      let hdir = r['handDir' + s] ? r['handDir' + s].clone() : fdir.clone();
      // 手首が不自然に折れないよう、前腕の向きとの差を 105 度までにする（床に手のひらをつくと手首は 90 度ほど反る）
      const ang = hdir.angleTo(fdir);
      if (ang > rad(105)) hdir = fdir.clone().lerp(hdir, rad(105) / ang).normalize();
      r['handD' + s] = hdir;
      let hup = r['handUp' + s] ? r['handUp' + s].clone() : r['elbowP' + s].clone();
      hup.addScaledVector(hdir, -hup.dot(hdir));
      if (hup.lengthSq() < 1e-6) hup.set(0, 1, 0);
      r['handU' + s] = hup.normalize();
      // 指: 付け根と第2関節で手のひら側へ曲げる（curl 0=まっすぐ、1=握る）。指定がなければ、床に手をつくほど伸ばす
      const palmN = hup.clone().negate();
      const near = smooth(1 - (arm.W.y - D.palmH) / 0.06);
      const curl = r['curl' + s] != null ? r['curl' + s] : lerpN(0.3, 0.04, near);
      const bend = (a) => hdir.clone().multiplyScalar(Math.cos(a)).addScaledVector(palmN, Math.sin(a));
      const a1 = curl * rad(80), a2 = a1 + curl * rad(95);
      r['knuck' + s] = arm.W.clone().addScaledVector(hdir, D.knuck);
      r['fingD' + s] = bend(a1);
      r['midj' + s] = r['knuck' + s].clone().addScaledVector(r['fingD' + s], D.fing1);
      r['tipD' + s] = bend(a2);
      const tr = body.thumbRest && body.thumbRest[s];
      if (tr) {
        const hx = new T.Vector3().crossVectors(hdir, palmN);
        const at = (v) => hx.clone().multiplyScalar(v[0]).addScaledVector(hdir, v[1]).addScaledVector(palmN, v[2]);
        r['thumbP' + s] = arm.W.clone().add(at(tr.off));
        // 床に手をつくと親指は手のひらの面に沿う。握るときは手のひら側へ巻く
        let pc = lerpN(tr.dir[2], 0.04, near);
        if (r['curl' + s] != null) pc = lerpN(pc, 0.75, r['curl' + s]);
        r['thumbD' + s] = at([tr.dir[0], tr.dir[1], pc]).normalize();
      }

      const leg = ik2(r['hip' + s], r['ankleT' + s], r['kneeP' + s], D.thigh, D.shin);
      r['knee' + s] = leg.E; r['ankle' + s] = leg.W;
      let toe;
      if (r['toeT' + s]) toe = r['toeT' + s].clone();
      else {
        // 目標がないとき、つま先はひざの向いている側（すねの前）へ。脚を前に伸ばしたときも足先がねじれない
        const shinD = leg.W.clone().sub(leg.E).normalize();
        const f = r['kneeP' + s].clone();
        f.addScaledVector(shinD, -f.dot(shinD));
        if (f.lengthSq() < 1e-4) { f.set(0, 0, 1).applyQuaternion(r.Q0); f.addScaledVector(shinD, -f.dot(shinD)); }
        if (f.lengthSq() < 1e-6) f.set(0, 0, 1);
        toe = leg.W.clone().addScaledVector(f.normalize(), D.foot);
      }
      // 足の長さを一定に（目標が遠くても足首から母指球まで 15cm）
      const fd = toe.sub(leg.W); fd.normalize();
      // 足首の可動域: 床についていない足は、すねに対して 背屈 25 度・底屈 65 度、つま先の向きは ひざより内へ 25 度・外へ 50 度まで。
      // （懸垂で足先が上を向く・後ろへねじれるなど、体の構造上できない向きにならないように）
      {
        const free = smooth((Math.min(toe.y + leg.W.y, leg.W.y * 2) / 2 - 0.05) / 0.05) * smooth((leg.W.y - D.ankleH - 0.02) / 0.04);
        if (free > 0) {
          const u = leg.E.clone().sub(leg.W).normalize();               // すねの上向き
          // すねの前: ひざが曲がっていれば太ももの向き（ひざの外側）、伸びていればひざの向き（kneeP）
          const sf = r['kneeP' + s].clone().addScaledVector(u, -r['kneeP' + s].dot(u)).normalize();
          const tdp = leg.E.clone().sub(r['hip' + s]).normalize(); tdp.addScaledVector(u, -tdp.dot(u));
          const kb = smooth(tdp.length() / 0.3);
          if (tdp.lengthSq() > 1e-6) sf.lerp(tdp.normalize(), kb);
          if (sf.lengthSq() > 1e-6) {
            sf.normalize();
            const out = new T.Vector3().crossVectors(u, sf).multiplyScalar(s === 'L' ? 1 : -1);
            let phi = Math.acos(clamp(fd.dot(u), -1, 1));
            const pr = fd.clone().addScaledVector(u, -fd.dot(u));
            let psi = pr.lengthSq() > 1e-6 ? Math.atan2(pr.dot(out), pr.dot(sf)) : 0;
            const phiC = clamp(phi, rad(65), rad(155)), psiC = clamp(psi, rad(-25), rad(50));
            phi = lerpN(phi, phiC, free); psi = lerpN(psi, psiC, free);
            if (pr.lengthSq() < 0.09 && phi < rad(70)) psi = lerpN(psi, 0, free); // 足がすねに沿うほど曲がっているときは、ねじれを戻す
            fd.copy(u).multiplyScalar(Math.cos(phi)).addScaledVector(sf, Math.sin(phi) * Math.cos(psi)).addScaledVector(out, Math.sin(phi) * Math.sin(psi)).normalize();
          }
        }
      }
      r['toe' + s] = leg.W.clone().addScaledVector(fd, D.foot);
      r['footD' + s] = fd;
      // 足の甲の向き: ふつうはすねの向き。足の裏が床に近く水平に近いときは真上へ寄せる（脚を開いても足の裏が床に平らにつく）
      const fz0 = new T.Vector3().subVectors(leg.E, leg.W).addScaledVector(fd, 0);
      fz0.addScaledVector(fd, -fz0.dot(fd)).normalize();
      const upW = new T.Vector3(0, 1, 0).addScaledVector(fd, -fd.y);
      // （すねが横に寝ているとき＝横向きに寝る種目では寄せない。足が横に倒れてしまうため）
      const flatW = smooth(1 - Math.abs(fd.y) / 0.6) * smooth(1 - (leg.W.y - D.ankleH) / 0.06) * (upW.lengthSq() > 1e-4 ? smooth((fz0.dot(upW.clone().normalize()) - 0.3) / 0.4) : 0);
      if (upW.lengthSq() > 1e-4 && flatW > 0) fz0.lerp(upW.normalize(), flatW).addScaledVector(fd, -fz0.dot(fd)).normalize();
      r['footZ' + s] = fz0;
      // 足の指: ふだんは足に対して立ったときと同じ角度。母指球が床に近いほど、指を床に沿わせる（つま先立ちで指が床にもぐらない）
      const rest = body.toeRest && body.toeRest[s];
      if (rest) {
        const fz = r['footZ' + s];
        const fx = new T.Vector3().crossVectors(fd, fz);
        // 指の付け根は蝶番。足の横の軸（fx）のまわりに、反らす（+）・曲げる（−）
        const dirAt = (th) => {
          const b = rest[1] * Math.cos(th) - rest[2] * Math.sin(th), c = rest[1] * Math.sin(th) + rest[2] * Math.cos(th);
          return fx.clone().multiplyScalar(rest[0]).addScaledVector(fd, b).addScaledVector(fz, c);
        };
        const ball = r['toe' + s];
        // 指先の高さの目標: 床から 1cm（指の厚み）。母指球が床に近いほど床に沿わせ、離れていれば立った姿勢の角度のまま
        const tipY = (th) => ball.y + dirAt(th).y * D.toe;
        const near = smooth(1 - (ball.y - D.ballH) / 0.05);
        let th = 0;
        if (near > 0 || tipY(0) < 0.01) {
          let best = 0, bestE = Infinity;
          for (let a = -0.5; a <= 1.75; a += 0.025) {
            const e = Math.abs(tipY(a) - 0.01) + Math.abs(a) * 0.002;
            if (e < bestE) { bestE = e; best = a; }
          }
          th = tipY(0) < 0.01 ? best : best * near;
        }
        r['toesD' + s] = dirAt(th).normalize();
      }
    }
    return r;
  }

  return { resolve, limbs, quatFromAxes };
}

// ---------------------------------------------------------------- 部位を関節の位置に合わせる
function makeRig(body) {
  const { parts } = body;
  const m = new T.Matrix4(), x = new T.Vector3(), y = new T.Vector3(), z = new T.Vector3();
  const place = (g, origin, ydir, zhint) => {
    y.copy(ydir).normalize();
    z.copy(zhint).addScaledVector(y, -zhint.dot(y));
    if (z.lengthSq() < 1e-8) z.set(y.y, -y.x, 0.2).addScaledVector(y, -0.2 * y.z);
    z.normalize();
    x.crossVectors(y, z);
    m.makeBasis(x, y, z);
    g.quaternion.setFromRotationMatrix(m);
    g.position.copy(origin);
  };
  const setQ = (g, origin, quat) => { g.position.copy(origin); g.quaternion.copy(quat); };
  const v = new T.Vector3(), w = new T.Vector3(), fd = new T.Vector3(), a1 = new T.Vector3(), a2 = new T.Vector3();
  const qa = new T.Quaternion(), qb = new T.Quaternion(), yAx = new T.Vector3(0, 1, 0);
  // 基準の姿勢での「上腕→前腕」「前腕→手」の向きの関係（人体モデルの BIND から）
  const rest = {};
  if (body.bind) for (const s of ['L', 'R']) {
    const q = (nm) => new T.Quaternion(...body.bind[nm].q);
    rest[s] = { uf: q('upper' + s).invert().multiply(q('fore' + s)), fhInv: q('fore' + s).invert().multiply(q('hand' + s)).invert() };
    if (body.bind['ush' + s]) rest[s].cu = q('clav' + s).invert().multiply(q('ush' + s));
  }
  return function apply(r) {
    setQ(parts.pelvis, r.P0, r.Q0);
    setQ(parts.abd, r.P0, r.Qa);
    setQ(parts.rib, r.W, r.Qr);
    setQ(parts.neck, r.N, r.Qr.clone().slerp(r.Qh, 0.5));
    setQ(parts.head, r.H, r.Qh);
    for (const s of ['L', 'R']) {
      // 鎖骨と肩甲骨: 鎖骨の付け根から肩関節へ
      if (parts['clav' + s]) place(parts['clav' + s], r['clv' + s], a1.subVectors(r['sh' + s], r['clv' + s]), a2.set(0, 0, 1).applyQuaternion(r.Qr));
      // 上腕の前（力こぶ側）＝前腕が曲がっていく側
      v.subVectors(r['elbow' + s], r['sh' + s]);
      w.subVectors(r['wrist' + s], r['elbow' + s]).addScaledVector(r['elbowP' + s], -0.04);
      place(parts['upper' + s], r['sh' + s], v, w);
      // 上腕の付け根: 鎖骨から見た基準の向きから、腕の向きへ最短で回すだけ（ねじれを持たない）
      if (parts['ush' + s] && rest[s] && rest[s].cu) {
        qa.multiplyQuaternions(parts['clav' + s].quaternion, rest[s].cu);
        a1.set(0, 1, 0).applyQuaternion(qa);
        qb.setFromUnitVectors(a1, a2.copy(v).normalize());
        parts['ush' + s].quaternion.multiplyQuaternions(qb, qa);
        parts['ush' + s].position.copy(r['sh' + s]);
      }
      // 前腕: 肘は蝶番（ちょうつがい）なので、上腕から見たねじれは基準の姿勢のまま、曲げる向きだけ変える
      fd.subVectors(r['wrist' + s], r['elbow' + s]).normalize();
      if (parts['twist' + s]) {
        const R = rest[s];
        qa.multiplyQuaternions(parts['upper' + s].quaternion, R.uf);
        a1.set(0, 1, 0).applyQuaternion(qa);
        qb.setFromUnitVectors(a1, fd);
        parts['fore' + s].quaternion.multiplyQuaternions(qb, qa);
        parts['fore' + s].position.copy(r['elbow' + s]);
      } else {
        a1.copy(z).addScaledVector(fd, -z.dot(fd)).normalize();
        a2.copy(r['handU' + s]).negate(); a2.addScaledVector(fd, -a2.dot(fd)).normalize();
        w.copy(a1).add(a2); if (w.lengthSq() < 1e-4) w.copy(a2);
        place(parts['fore' + s], r['elbow' + s], fd, w);
      }
      place(parts['hand' + s], r['wrist' + s], r['handD' + s], w.copy(r['handU' + s]).negate());
      // 前腕を回す（手のひらを返す）ねじれは、ねじれ用の骨が半分受け持ち、前腕の途中から手首にかけて少しずつ回す
      if (parts['twist' + s]) {
        const R = rest[s], qF = parts['fore' + s].quaternion;
        qa.copy(qF).invert().multiply(parts['hand' + s].quaternion).multiply(R.fhInv); // 基準からの手の回り（前腕から見て）
        if (qa.w < 0) { qa.x = -qa.x; qa.y = -qa.y; qa.z = -qa.z; qa.w = -qa.w; }
        const ang = 2 * Math.atan2(qa.y, qa.w); // 前腕の向き（y 軸）まわりの成分だけ
        qb.setFromAxisAngle(yAx, ang / 2);
        parts['twist' + s].quaternion.multiplyQuaternions(qF, qb);
        parts['twist' + s].position.copy(r['elbow' + s]);
      }
      if (parts['fing' + s]) {
        place(parts['fing' + s], r['knuck' + s], r['fingD' + s], w.copy(r['handU' + s]).negate());
        place(parts['tip' + s], r['midj' + s], r['tipD' + s], w.copy(r['handU' + s]).negate());
      }
      if (parts['thumb' + s] && r['thumbD' + s]) place(parts['thumb' + s], r['thumbP' + s], r['thumbD' + s], w.copy(r['handU' + s]).negate());
      v.subVectors(r['knee' + s], r['hip' + s]);
      w.subVectors(r['knee' + s], r['ankle' + s]).addScaledVector(r['kneeP' + s], 0.04);
      w.addScaledVector(v.clone().normalize(), -w.dot(v.clone().normalize()));
      place(parts['thigh' + s], r['hip' + s], v, w);
      place(parts['shin' + s], r['knee' + s], v.subVectors(r['ankle' + s], r['knee' + s]), r['kneeP' + s]);
      // 足: Y が足首→つま先、Z（甲）がすねの方向
      place(parts['foot' + s], r['ankle' + s], r['footD' + s], r['footZ' + s]);
      if (parts['toes' + s] && r['toesD' + s]) place(parts['toes' + s], r['toe' + s], r['toesD' + s], z.clone());
    }
    if (body.syncDQ) body.syncDQ();
  };
}

// ---------------------------------------------------------------- 道具（床マット・椅子・鉄棒・壁）
const MAT_T = 0.012;
function buildProps(scene) {
  const g = {};
  const mk = (geo, color, o = {}) => {
    const mesh = new T.Mesh(geo, new T.MeshPhysicalMaterial({ color, roughness: 0.6, metalness: 0, ...o }));
    mesh.castShadow = true; mesh.receiveShadow = true; return mesh;
  };
  // マット
  g.mat = new T.Group();
  // 上面が y=0（手足の接地の高さ）。マットを敷くときは床をマットの厚みだけ下げる
  const mat = mk(new T.BoxGeometry(0.66, MAT_T, 1.85), 0x2a2a2c, { roughness: 0.92 });
  mat.position.y = -MAT_T / 2; mat.castShadow = false; g.mat.add(mat);
  // 箱（椅子・ベンチ）: 座面の高さ 0.45
  g.box = new T.Group();
  const seat = mk(new T.BoxGeometry(0.5, 0.05, 0.42), 0x2c2c2e, { roughness: 0.5 });
  seat.position.y = 0.425; g.box.add(seat);
  for (const [px, pz] of [[0.21, 0.17], [-0.21, 0.17], [0.21, -0.17], [-0.21, -0.17]]) {
    const leg = mk(new T.CylinderGeometry(0.018, 0.018, 0.4, 12), 0x9a9a9c, { metalness: 0.85, roughness: 0.3 });
    leg.position.set(px, 0.2, pz); g.box.add(leg);
  }
  // 鉄棒（高さは種目で変える）
  g.bar = new T.Group();
  const barMat = { metalness: 0.95, roughness: 0.22 };
  const bar = mk(new T.CylinderGeometry(0.016, 0.016, 1.3, 20), 0xd2d2d2, barMat);
  bar.rotation.z = Math.PI / 2; g.bar.add(bar); g.bar.userData.bar = bar;
  g.bar.userData.posts = [];
  for (const s of [1, -1]) {
    const post = mk(new T.CylinderGeometry(0.025, 0.025, 1, 16), 0x3a3a3c, { metalness: 0.7, roughness: 0.35 });
    post.position.x = 0.65 * s; g.bar.add(post); g.bar.userData.posts.push(post);
  }
  // 壁
  g.wall = new T.Group();
  const wall = mk(new T.BoxGeometry(1.6, 1.9, 0.06), 0xededeb, { roughness: 0.95 });
  wall.position.y = 0.95; wall.castShadow = false; g.wall.add(wall);
  for (const k of Object.keys(g)) { g[k].visible = false; scene.add(g[k]); }
  // 道具を置き、床の高さを返す（マットがあるときはマットの下）
  return function show(props) {
    for (const k of Object.keys(g)) g[k].visible = false;
    if (!props) return 0;
    const ground = props.mat ? -MAT_T : 0;
    for (const [k, o] of Object.entries(props)) {
      const obj = g[k]; if (!obj) continue;
      obj.visible = true;
      obj.position.set(...(o.p || [0, 0, 0]));
      obj.rotation.set(0, rad(o.yaw || 0), 0);
      if (k === 'bar') {
        const h = o.h || 2.2;
        obj.position.y = h;
        for (const post of obj.userData.posts) { post.scale.y = h - ground; post.position.y = -(h - ground) / 2; }
      }
      // 箱と壁は床から立てる（座面の高さは変えない）
      if (k === 'box') { obj.position.y = ground; obj.scale.y = ((o.h || 0.45) - ground) / 0.45; }
      if (k === 'wall') obj.position.y = ground;
    }
    return ground;
  };
}

// ---------------------------------------------------------------- スタジオ（光・床・反射）
// 見た目ごとの光（環境・半球・主光・補助・輪郭）と床の色
const LOOK = {
  mannequin: { env: 0.55, hemi: 0.55, key: 2.3, keyC: 0xffffff, fill: 0.7, rim: 1.5, rimC: 0xffffff, floor: ['255,255,255', '236,239,243'], shadow: 0.2 },
  real: { env: 0.5, hemi: 0.45, key: 2.5, keyC: 0xfff0e2, fill: 0.55, rim: 1.8, rimC: 0xe8f0ff, floor: ['255,255,255', '238,236,233'], shadow: 0.24 },
  clay: { env: 0.42, hemi: 0.4, key: 2.9, keyC: 0xffffff, fill: 0.45, rim: 1.6, rimC: 0xffffff, floor: ['255,255,255', '232,234,237'], shadow: 0.26 },
  dark: { env: 0.34, hemi: 0.32, key: 2.0, keyC: 0xffffff, fill: 0.38, rim: 2.4, rimC: 0xffffff, floor: ['255,255,255', '236,236,234'], shadow: 0.26 },
};
// 映り込み用の部屋（見た目ごとに1回だけ作る）
const ENV = {};
function envMap(renderer) {
  const pm = new T.PMREMGenerator(renderer);
  const env = new T.Scene();
  const room = new T.Mesh(new T.BoxGeometry(12, 8, 12), new T.MeshBasicMaterial({ color: 0x949494, side: T.BackSide }));
  env.add(room);
  const panel = (w, h, pos, rot, c) => {
    const p = new T.Mesh(new T.PlaneGeometry(w, h), new T.MeshBasicMaterial({ color: c, side: T.DoubleSide }));
    p.position.set(...pos); p.rotation.set(...rot); env.add(p);
  };
  panel(6, 3, [0, 3.9, 0], [Math.PI / 2, 0, 0], 0xffffff);
  panel(3, 4, [5.9, 1.5, 1], [0, -Math.PI / 2, 0], 0xf4f4f4);
  panel(3, 4, [-5.9, 2, -1], [0, Math.PI / 2, 0], 0xdedede);
  panel(8, 1.2, [0, 0.8, -5.9], [0, 0, 0], 0xfff3e6);
  const tex = pm.fromScene(env, 0.035).texture;
  pm.dispose();
  return tex;
}

function buildStage(renderer, scene, style = 'mannequin') {
  const L = LOOK[style] || LOOK.mannequin;
  scene.environment = ENV[style] || (ENV[style] = envMap(renderer));
  scene.environmentIntensity = L.env;

  scene.add(new T.HemisphereLight(0xffffff, 0xb0b0b0, L.hemi));

  const key = new T.DirectionalLight(L.keyC, L.key);
  key.position.set(2.2, 4.2, 2.8);
  key.castShadow = true;
  key.shadow.mapSize.set(1024, 1024);
  const sc = key.shadow.camera; sc.left = -2.4; sc.right = 2.4; sc.top = 2.8; sc.bottom = -2.4; sc.near = 0.5; sc.far = 12;
  key.shadow.bias = -0.0003; key.shadow.normalBias = 0.03; key.shadow.radius = 2.5;
  scene.add(key); scene.add(key.target);
  const fill = new T.DirectionalLight(0xffffff, L.fill); fill.position.set(-3, 1.8, 1.5); scene.add(fill);
  const rim = new T.DirectionalLight(L.rimC, L.rim); rim.position.set(-1.2, 3, -4); scene.add(rim);
  const rim2 = new T.DirectionalLight(L.rimC, L.rim * 0.5); rim2.position.set(2.5, 2, -3); scene.add(rim2);

  // 床: 影だけを落とす面＋中央が少し明るい丸い床
  const c = document.createElement('canvas'); c.width = c.height = 256;
  const x = c.getContext('2d');
  const gr = x.createRadialGradient(128, 128, 0, 128, 128, 128);
  gr.addColorStop(0, `rgba(${L.floor[0]},0.95)`); gr.addColorStop(0.55, `rgba(${L.floor[1]},0.7)`); gr.addColorStop(1, `rgba(${L.floor[1]},0)`);
  x.fillStyle = gr; x.fillRect(0, 0, 256, 256);
  const tex = new T.CanvasTexture(c); tex.colorSpace = T.SRGBColorSpace;
  const disc = new T.Mesh(new T.CircleGeometry(3.2, 64), new T.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false }));
  disc.rotation.x = -Math.PI / 2; disc.position.y = -0.001; scene.add(disc);
  const shadow = new T.Mesh(new T.PlaneGeometry(12, 12), new T.ShadowMaterial({ opacity: L.shadow }));
  shadow.rotation.x = -Math.PI / 2; shadow.receiveShadow = true; scene.add(shadow);
  return { key, ground: (y) => { disc.position.y = y - 0.001; shadow.position.y = y; } };
}

// ---------------------------------------------------------------- プレーヤー
const VIEWS = { diag: [40, 72], side: [90, 80], front: [0, 80], back: [180, 78], top: [55, 28] };
const VIEW_LABEL = { diag: '斜め', side: '横', front: '正面', back: '後ろ', top: '上' };
const ICON = {
  play: '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M7 4.5v15l12.5-7.5z" fill="currentColor"/></svg>',
  pause: '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M6.5 4.5h4v15h-4zM13.5 4.5h4v15h-4z" fill="currentColor"/></svg>',
  full: '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" fill="none" stroke="currentColor" stroke-width="2"/></svg>',
  voice: '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z" fill="currentColor"/><path d="M15.5 8.8a4.5 4.5 0 0 1 0 6.4M18 6.5a8 8 0 0 1 0 11" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
  mute: '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z" fill="currentColor"/><path d="M16 9.5l5 5M21 9.5l-5 5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
  view: '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M12 3.2 19.6 7.6v8.8L12 20.8 4.4 16.4V7.6z M4.4 7.6 12 12l7.6-4.4M12 12v8.8" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>',
  muscle: '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><circle cx="12" cy="4.6" r="2.1" fill="currentColor"/><path d="M6 8.6c2.2.9 3.9 1.1 6 1.1s3.8-.2 6-1.1M12 9.7v5.1M12 14.8l-2.6 5.6M12 14.8l2.6 5.6" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/></svg>',
  check: '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  exit: '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M9 4v5H4M20 9h-5V4M15 20v-5h5M4 15h5v5" fill="none" stroke="currentColor" stroke-width="2"/></svg>',
};

// ---------------------------------------------------------------- 描画の共有
// 1ページに 3D が何個あっても、WebGL の描画器は1つだけ作り、各プレーヤーの 2D キャンバスへ写す。
// 形・映り込み・シェーダーも1回ずつで済み、スマホのメモリと読み込み時間を節約する。
// 動かす（毎フレーム描く）のは、いちばん画面に出ているプレーヤー1つだけ。ほかは止めた絵を出す。
const GFX = { r: null, w: 0, h: 0, pr: 0, players: new Set() };
const touchUI = typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches;
// 画素の細かさ: 高精細の画面でも 1.5 倍まで（スマホは 1.25 倍から）。重い端末では自動で下げる
const Q = { pr: Math.min(typeof devicePixelRatio === 'number' ? devicePixelRatio : 1, touchUI ? 1.25 : 1.5), slow: 0, n: 0 };
function gfx() {
  if (GFX.r) return GFX.r;
  const r = GFX.r = new T.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance', preserveDrawingBuffer: false });
  r.outputColorSpace = T.SRGBColorSpace;
  r.toneMapping = T.ACESFilmicToneMapping; r.toneMappingExposure = 1.02;
  r.shadowMap.enabled = true; r.shadowMap.type = T.PCFShadowMap;
  r.setClearColor(0x000000, 0);
  return r;
}
function activePlayer() {
  let best = null, score = -1;
  for (const p of GFX.players) {
    if (!p.mv || !p.visible || p.ratio <= 0) continue;
    const s = p.ratio + (p.touchedAt ? 1 + p.touchedAt / 1e13 : 0); // 触ったプレーヤーを優先
    if (s > score) { score = s; best = p; }
  }
  return best;
}
function reselect() {
  const a = activePlayer();
  if (a !== GFX.active) { GFX.active = a; if (a) a.kick(); }
}

const REDUCED = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

class Player {
  constructor(el, opts = {}) {
    this.el = el;
    this.opts = opts;
    this.speed = 1;
    this.playing = !matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.highlight = true;
    this.style = opts.style || el.dataset.style || 'dark';
    if (this.style !== 'mannequin' && !STYLES[this.style]) this.style = 'dark';
    this.view = null;
    this.t = 0; this.reps = 0;
    this.visible = true; this.ratio = 0;
    this.ui();
  }

  ui() {
    const el = this.el;
    el.classList.add('j3d', 'j3d--' + this.style);
    el.innerHTML = `
      <div class="j3d-stage"><canvas class="j3d-canvas" aria-hidden="true"></canvas>
        <div class="j3d-load" role="status">JIJUくんを呼んでいます…</div>
        <div class="j3d-head"><p class="j3d-name"></p><p class="j3d-en"></p></div>
        <div class="j3d-count" aria-live="off"><span class="j3d-count__n">0</span><span class="j3d-count__u">回</span></div>
        <div class="j3d-phase"><span class="j3d-phase__label"></span><span class="j3d-phase__bar"><i></i></span></div>
        <p class="j3d-hint">ドラッグで回転・2本指で拡大</p>
        <button type="button" class="j3d-full" aria-label="全画面で見る"></button>
      </div>
      <div class="j3d-bar" role="toolbar" aria-label="3Dの操作">
        <button type="button" class="j3d-tool j3d-play"><span class="j3d-ic"></span><span class="j3d-lb"></span></button>
        <div class="j3d-mw">
          <button type="button" class="j3d-tool j3d-viewbtn" aria-haspopup="menu" aria-expanded="false"><span class="j3d-ic">${ICON.view}</span><span class="j3d-lb">斜め</span></button>
          <div class="j3d-menu" role="menu" aria-label="見る向き" hidden></div>
        </div>
        <button type="button" class="j3d-tool j3d-speed"><span class="j3d-ic j3d-ic--txt">1×</span><span class="j3d-lb">速さ</span></button>
        <button type="button" class="j3d-tool j3d-mus is-on" aria-pressed="true"><span class="j3d-ic">${ICON.muscle}</span><span class="j3d-lb">筋肉</span></button>
        <button type="button" class="j3d-tool j3d-voice" aria-pressed="false"${NARRATION ? '' : ' hidden'}><span class="j3d-ic"></span><span class="j3d-lb">解説</span></button>
      </div>
      <div class="j3d-legend"></div>`;
    this.$ = (s) => el.querySelector(s);
    this.canvas = this.$('.j3d-canvas');
    this.ctx = null;
    el.addEventListener('pointerdown', () => { this.touchedAt = Date.now(); reselect(); }, true);
    this.$('.j3d-play').addEventListener('click', () => this.toggle());
    this.$('.j3d-bar').style.setProperty('--n', NARRATION ? 5 : 4);
    // 速さ: 押すたびに 1× → 1.5× → 0.5× と切り替える
    const sp = this.$('.j3d-speed');
    const syncSpeed = () => { sp.querySelector('.j3d-ic').textContent = this.speed + '×'; sp.setAttribute('aria-label', `速さ ${this.speed}倍。押すと切り替え`); };
    sp.addEventListener('click', () => { const order = [1, 1.5, 0.5]; this.speed = order[(order.indexOf(this.speed) + 1) % order.length]; syncSpeed(); });
    syncSpeed();
    // 視点: 押すと小さなメニューが開く（斜め・横・正面・後ろ・上）
    const vb = this.$('.j3d-viewbtn'), menu = this.$('.j3d-menu');
    menu.innerHTML = Object.keys(VIEWS).map((k) => `<button type="button" role="menuitemradio" aria-checked="false" data-v="${k}"><span>${VIEW_LABEL[k]}</span>${ICON.check}</button>`).join('');
    const openMenu = (open) => {
      menu.hidden = !open; vb.setAttribute('aria-expanded', String(open)); vb.classList.toggle('is-open', open);
      if (open) (menu.querySelector('[aria-checked="true"]') || menu.firstElementChild).focus();
    };
    vb.addEventListener('click', () => openMenu(menu.hidden));
    menu.addEventListener('click', (ev) => { const b = ev.target.closest('button'); if (!b) return; this.setView(b.dataset.v); openMenu(false); vb.focus(); });
    menu.addEventListener('keydown', (ev) => {
      const items = [...menu.children], i = items.indexOf(document.activeElement);
      if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') { ev.preventDefault(); items[(i + (ev.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length].focus(); }
      if (ev.key === 'Escape') { openMenu(false); vb.focus(); }
    });
    document.addEventListener('pointerdown', (ev) => { if (!menu.hidden && !ev.target.closest('.j3d-mw')) openMenu(false); });
    // 筋肉: 効いている筋肉を光らせる／消す
    const mb = this.$('.j3d-mus');
    const syncMus = () => { mb.classList.toggle('is-on', this.highlight); mb.setAttribute('aria-pressed', String(this.highlight)); mb.setAttribute('aria-label', this.highlight ? '効いている筋肉を表示中。押すと消す' : '効いている筋肉を表示する'); };
    mb.addEventListener('click', () => { this.highlight = !this.highlight; syncMus(); this.kick(); });
    syncMus();
    // 解説ナレーション: 押すと、いまの種目の解説を最初から読む（もう一度押すと止める）
    if (NARRATION) this.$('.j3d-voice').addEventListener('click', () => (this.voiceOn ? this.stopVoice() : this.playVoice()));
    this.syncVoice && this.syncVoice(false);
    // 全画面: 使える端末は Fullscreen API、使えない端末（iPhone など）は画面いっぱいに固定して表示
    const fb = this.$('.j3d-full');
    const isFull = () => document.fullscreenElement === el || el.classList.contains('is-full');
    const syncFull = () => { const f = isFull(); fb.innerHTML = f ? ICON.exit : ICON.full; fb.setAttribute('aria-label', f ? '全画面を閉じる' : '全画面で見る'); this.resize && this.renderer && this.resize(); };
    fb.addEventListener('click', () => {
      if (isFull()) {
        if (document.fullscreenElement) document.exitFullscreen();
        el.classList.remove('is-full'); document.documentElement.classList.remove('jj-lock');
      } else if (el.requestFullscreen && document.fullscreenEnabled) el.requestFullscreen().catch(() => { el.classList.add('is-full'); document.documentElement.classList.add('jj-lock'); syncFull(); });
      else { el.classList.add('is-full'); document.documentElement.classList.add('jj-lock'); }
      syncFull();
    });
    document.addEventListener('fullscreenchange', syncFull);
    document.addEventListener('keydown', (ev) => { if (ev.key === 'Escape' && el.classList.contains('is-full')) { el.classList.remove('is-full'); document.documentElement.classList.remove('jj-lock'); syncFull(); } });
    // スペースキーで再生・一時停止（プレーヤーの中にフォーカスがあるとき）
    el.addEventListener('keydown', (ev) => { if (ev.key === ' ' && ev.target === this.canvas) { ev.preventDefault(); this.toggle(); } });
    this.canvas.tabIndex = 0;
    this.canvas.setAttribute('aria-label', 'JIJUくんの3Dアニメーション。ドラッグで回転できます');
    this.canvas.removeAttribute('aria-hidden');
    syncFull();
    this.syncPlay();
  }

  syncPlay() {
    const b = this.$('.j3d-play');
    b.querySelector('.j3d-ic').innerHTML = this.playing ? ICON.pause : ICON.play;
    b.querySelector('.j3d-lb').textContent = this.playing ? '一時停止' : '再生';
    b.setAttribute('aria-label', this.playing ? '一時停止' : '再生');
  }
  playVoice() {
    if (!this.id) return;
    if (VOICE && VOICE !== this) VOICE.stopVoice();
    VOICE = this;
    const a = this.audio || (this.audio = new Audio());
    a.preload = 'auto';
    a.onended = () => this.syncVoice(false);
    a.onerror = () => this.syncVoice(false);
    if (!a.src.endsWith(`jiju-voice-${this.id}.mp3`)) a.src = voiceUrl(this.id);
    a.currentTime = 0;
    // 解説に合わせて、動きも最初から見せる
    this.t = 0; this.reps = 0; this.holdSec = 0;
    if (!this.playing) { this.playing = true; this.syncPlay(); }
    this.touchedAt = Date.now(); reselect(); this.kick();
    a.play().then(() => this.syncVoice(true), () => this.syncVoice(false));
    this.syncVoice(true);
  }
  stopVoice() {
    if (this.audio) this.audio.pause();
    if (VOICE === this) VOICE = null;
    this.syncVoice(false);
  }
  syncVoice(on) {
    this.voiceOn = on;
    const b = this.$('.j3d-voice');
    b.querySelector('.j3d-ic').innerHTML = on ? ICON.voice : ICON.mute;
    b.classList.toggle('is-on', on);
    b.setAttribute('aria-pressed', String(on));
    b.setAttribute('aria-label', on ? '解説を止める' : 'JIJUくんの解説を聞く');
    this.el.classList.toggle('is-voice', on);
  }

  toggle() { this.playing = !this.playing; this.touchedAt = Date.now(); this.syncPlay(); reselect(); if (this.playing) this.kick(); }

  async init() {
    const { MOVES: M } = await deps(this.style);
    this.M = M;
    const r = this.renderer = gfx();
    this.ctx = this.canvas.getContext('2d');
    this.scene = new T.Scene();
    this.camera = new T.PerspectiveCamera(30, 1, 0.05, 40);
    this.stage = buildStage(r, this.scene, this.style);
    this.body = this.style === 'mannequin' ? buildBody(this.scene) : buildSkinned(this.scene, BODY, STYLES[this.style]);
    this.rig = makeRig(this.body);
    this.solver = makeSolver(this.body);
    this.props = buildProps(this.scene);
    this.orbit = { th: 40, ph: 72, dist: 4, tgt: new T.Vector3(0, 0.8, 0), cur: null };
    this.controls();
    new ResizeObserver(() => this.resize()).observe(this.$('.j3d-stage'));
    this.resize();
    this.io = new IntersectionObserver((es) => {
      const e = es[es.length - 1];
      this.visible = e.isIntersecting; this.ratio = e.isIntersecting ? e.intersectionRatio : 0;
      reselect(); if (this.visible) this.kick();
    }, { threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] });
    this.io.observe(this.el);
    GFX.players.add(this);
    // シェーダーの組み立てを先に済ませる（使える端末では裏で組み立て、画面が固まらない）
    if (r.compileAsync) { try { await r.compileAsync(this.scene, this.camera); } catch (_) { /* 失敗しても最初の描画で組み立てる */ } }
    this.el.classList.add('is-ready');
    this.load(this.opts.ex || this.el.dataset.ex || 'push-up');
  }

  load(id) {
    const mv = this.M[id];
    if (!mv) { this.$('.j3d-load').textContent = 'この種目の3Dは準備中です'; return; }
    this.mv = mv; this.id = id; this.t = 0; this.reps = 0;
    this.$('.j3d-name').textContent = mv.name;
    this.$('.j3d-en').textContent = mv.en || '';
    this.el.classList.toggle('is-hold', !!mv.hold);
    this.$('.j3d-count__u').textContent = mv.hold ? '秒' : '回';
    // 効く筋肉
    const row = (h, ids, cls) => (ids && ids.length ? `<p class="j3d-lg ${cls}"><span class="j3d-legend__h">${h}</span><span>${ids.map((m) => MUSCLES[m]).join('・')}</span></p>` : '');
    this.$('.j3d-legend').innerHTML = row('主に効く', mv.primary, 'is-p') + row('補助', mv.secondary, 'is-s') + (NARRATION ? `<p class="j3d-credit">解説の声: ${VOICE_CREDIT}</p>` : '');
    this.roles = MIDS.map((k) => ((mv.primary || []).includes(k) ? 2 : (mv.secondary || []).includes(k) ? 1 : 0));
    this.paintSig = '';
    // 動きの範囲から、カメラの距離と注視点を決める
    const box = new T.Box3();
    const pts = ['P0', 'H', 'shL', 'shR', 'elbowL', 'elbowR', 'wristL', 'wristR', 'kneeL', 'kneeR', 'ankleL', 'ankleR', 'toeL', 'toeR'];
    for (let i = 0; i < 16; i++) {
      const r = this.solver.limbs(this.solver.resolve(mv.pose(i / 16, P)));
      for (const k of pts) box.expandByPoint(r[k]);
    }
    if (mv.bounds) for (const b of mv.bounds) box.expandByPoint(new T.Vector3(...b));
    box.expandByScalar(0.12);
    const c = box.getCenter(new T.Vector3()); const sz = box.getSize(new T.Vector3());
    this.fit = { c, size: sz };
    this.orbit.tgt.copy(c); this.orbit.tgt.y = Math.max(0.3, c.y - 0.03);
    this.setView(mv.view || 'diag', true);
    this.frame(0);
    this.$('.j3d-load').hidden = true;
    if (NARRATION && this.voiceOn) this.playVoice();
    reselect();
    this.kick();
  }

  setView(v, now) {
    this.view = v;
    const [th, ph] = VIEWS[v] || VIEWS.diag;
    let a = th + (this.mv && this.mv.yaw || 0);
    // いちばん近い回り方で回す
    const d = ((a - this.orbit.th) % 360 + 540) % 360 - 180;
    this.orbit.th += d; this.orbit.ph = ph;
    // 体と道具が画面に収まる距離（縦と横のどちらかで決まる）
    const vf = rad(this.camera.fov) / 2;
    const hf = Math.atan(Math.tan(vf) * (this.camera.aspect || 1.6));
    let need = 4;
    if (this.fit) {
      const s = this.fit.size, th2 = rad(this.orbit.th), ph2 = rad(ph);
      const across = Math.abs(Math.cos(th2)) * s.x + Math.abs(Math.sin(th2)) * s.z;
      const depth = Math.abs(Math.sin(th2)) * s.x + Math.abs(Math.cos(th2)) * s.z;
      const tall = s.y * Math.sin(ph2) + depth * Math.abs(Math.cos(ph2));
      need = Math.max((tall / 2) / Math.tan(vf), (across / 2) / Math.tan(hf)) + depth * 0.45;
    }
    this.orbit.dist = clamp(need, 1.8, 10);
    for (const b of this.$('.j3d-menu').children) b.setAttribute('aria-checked', String(b.dataset.v === v));
    this.$('.j3d-viewbtn .j3d-lb').textContent = VIEW_LABEL[v] || '視点';
    this.$('.j3d-viewbtn').setAttribute('aria-label', `見る向き: ${VIEW_LABEL[v] || '自由'}。押すと選べる`);
    if (now) this.orbit.cur = null;
    this.kick();
  }

  controls() {
    const cv = this.canvas; const o = this.orbit;
    const pts = new Map(); let pinch = 0;
    cv.style.touchAction = 'pan-y';
    cv.addEventListener('pointerdown', (e) => { pts.set(e.pointerId, [e.clientX, e.clientY]); cv.setPointerCapture(e.pointerId); this.el.classList.add('is-touched'); });
    cv.addEventListener('pointermove', (e) => {
      if (!pts.has(e.pointerId)) return;
      const [px, py] = pts.get(e.pointerId);
      if (pts.size === 1) {
        o.th -= (e.clientX - px) * 0.45; o.ph = clamp(o.ph - (e.clientY - py) * 0.3, 12, 100);
        for (const b of this.$('.j3d-menu').children) b.setAttribute('aria-checked', 'false');
        this.$('.j3d-viewbtn .j3d-lb').textContent = '自由';
      } else if (pts.size === 2) {
        const arr = [...pts.values()];
        const d = Math.hypot(arr[0][0] - arr[1][0], arr[0][1] - arr[1][1]);
        if (pinch) o.dist = clamp(o.dist * pinch / d, 1.6, 10);
        pinch = d;
      }
      pts.set(e.pointerId, [e.clientX, e.clientY]);
      this.kick();
    });
    const up = (e) => { pts.delete(e.pointerId); pinch = 0; };
    cv.addEventListener('pointerup', up); cv.addEventListener('pointercancel', up);
    cv.addEventListener('wheel', (e) => { if (!e.ctrlKey && !e.metaKey) return; e.preventDefault(); o.dist = clamp(o.dist * (1 + e.deltaY * 0.001), 1.6, 10); this.kick(); }, { passive: false });
  }

  resize() {
    const st = this.$('.j3d-stage');
    const w = st.clientWidth, h = st.clientHeight;
    if (!w || !h) return;
    this.w = w; this.h = h;
    const cw = Math.round(w * Q.pr), ch = Math.round(h * Q.pr);
    const sized = this.canvas.width !== cw || this.canvas.height !== ch;
    if (sized) { this.canvas.width = cw; this.canvas.height = ch; }
    this.camera.aspect = w / h; this.camera.updateProjectionMatrix();
    if (this.view) this.setView(this.view);
    if (sized && this.mv) this.frame(0); // 大きさを変えると絵が消えるので、画面の外でも1枚描き直す
    this.kick();
  }

  kick() {
    if (this.raf || !this.renderer) return;
    this.last = 0;
    this.raf = requestAnimationFrame((n) => this.tick(n));
  }

  tick(now) {
    this.raf = 0;
    const dt = this.last ? clamp((now - this.last) / 1000, 0, 0.1) : 0; this.last = now;
    if (!this.visible || document.hidden) return;
    let moving = false;
    const live = GFX.active === this;
    if (this.playing && this.mv && live) {
      moving = true;
      this.t += dt * this.speed / this.mv.dur;
      if (this.t >= 1) { this.t -= 1; this.reps++; }
      if (this.mv.hold) this.holdSec = (this.holdSec || 0) + dt * this.speed;
    }
    const camMoving = this.frame(dt);
    if (moving) this.adapt(dt);
    // adapt() の描き直しで kick() 済みのことがあるので、二重に回さない
    if ((moving || camMoving) && !this.raf) this.raf = requestAnimationFrame((n) => this.tick(n));
  }

  // 重い端末では画素の細かさを自動で下げる（1秒あたり 40 コマを切り続けたら 1 段下げる。下限 0.75 倍）
  adapt(dt) {
    if (!dt || Q.pr <= 0.75) return;
    Q.n++; if (dt > 1 / 40) Q.slow++;
    if (Q.n < 45) return;
    const slow = Q.slow / Q.n > 0.5;
    Q.n = 0; Q.slow = 0;
    if (!slow) return;
    Q.pr = Math.max(0.75, Math.round((Q.pr - 0.25) * 100) / 100);
    for (const p of GFX.players) p.resize();
  }

  frame(dt) {
    const mv = this.mv; if (!mv) return false;
    const r = this.solver.limbs(this.solver.resolve(mv.pose(this.t, P)));
    this.rig(r);
    const ground = this.props(r.props || mv.props);
    if (ground !== this.ground) { this.ground = ground; this.stage.ground(ground); }
    // 筋肉の光り方（効いている感じをグラデーションで見せる）
    //  effort（jiju-moves.js）: 休む 0.5 → 下ろす（伸ばされながら耐える）0.6〜0.85 → 縮む区間のまん中 1 → 休む姿勢へ少しずつ 0.5
    //  g = 0〜1 に直し、さらに時間方向になめらかに追いかける（明るくなるときは 0.2 秒、消えるときは 0.45 秒で寄せる）
    //  キープ種目は、ゆっくり息をするように明るさがわずかに揺れる
    const eff = mv.effort ? mv.effort(this.t, P) : 1;
    const g = clamp((eff - 0.5) / 0.5, 0, 1);
    this.clock = (this.clock || 0) + (this.playing ? dt : 0);
    const breath = mv.hold && !REDUCED ? 0.92 + 0.08 * Math.cos(this.clock * Math.PI * 2 * 0.45) : 1;
    const lv = this.level || (this.level = new Float32Array(MIDS.length));
    const tint = this.tint || (this.tint = MIDS.map(() => new T.Color()));
    let busy = false;
    for (let i = 0; i < MIDS.length; i++) {
      const role = this.highlight ? this.roles[i] : 0;
      const goal = role === 2 ? (0.12 + 0.98 * smooth(g)) * breath : role === 1 ? (0.08 + 0.52 * smooth(g)) * breath : 0;
      if (!dt) lv[i] = goal;
      else lv[i] += (goal - lv[i]) * (1 - Math.exp(-dt / (goal > lv[i] ? 0.2 : 0.45)));
      if (Math.abs(goal - lv[i]) > 0.004) busy = true;
      tint[i].set(role === 2 ? COL.prim : COL.sec);
    }
    if (this.body.setLevels) this.body.setLevels(lv, tint);
    else {
      const sig = lv.join(',');
      if (sig !== this.paintSig) { paint(this.body, lv, tint); this.paintSig = sig; }
    }
    // 表示: 回数・段階
    const ph = (mv.phases || []).filter((p) => p[0] <= this.t + 1e-6).pop();
    const phl = ph ? ph[1] : '', cnt = mv.hold ? String(Math.floor(this.holdSec || 0)) : String(this.reps), bar = this.t.toFixed(2);
    if (phl !== this.uiPh) { this.uiPh = phl; this.$('.j3d-phase__label').textContent = phl; }
    if (cnt !== this.uiCnt) { this.uiCnt = cnt; this.$('.j3d-count__n').textContent = cnt; }
    if (bar !== this.uiBar) { this.uiBar = bar; (this.barEl || (this.barEl = this.$('.j3d-phase__bar i'))).style.transform = `scaleX(${bar})`; }
    // カメラ（なめらかに追いかける）
    const o = this.orbit;
    const goal = [o.th, o.ph, o.dist];
    if (!o.cur) o.cur = goal.slice();
    const k = dt ? 1 - Math.exp(-dt * 7) : 1;
    let moving = false;
    for (let i = 0; i < 3; i++) { const d = goal[i] - o.cur[i]; if (Math.abs(d) > 1e-3) moving = true; o.cur[i] += d * k; }
    const th = rad(o.cur[0]), ph2 = rad(o.cur[1]), dist = o.cur[2];
    this.camera.position.set(o.tgt.x + dist * Math.sin(ph2) * Math.sin(th), o.tgt.y + dist * Math.cos(ph2), o.tgt.z + dist * Math.sin(ph2) * Math.cos(th));
    this.camera.lookAt(o.tgt);
    this.stage.key.target.position.copy(o.tgt);
    this.draw();
    return moving || busy; // 光が目標に追いつくまでは描き続ける
  }

  // 共有の描画器で描いて、このプレーヤーのキャンバスへ写す
  draw(camera = this.camera) {
    const r = this.renderer, cv = this.canvas;
    if (!this.w || !cv.width) return;
    if (GFX.w !== this.w || GFX.h !== this.h || GFX.pr !== Q.pr) {
      GFX.w = this.w; GFX.h = this.h; GFX.pr = Q.pr;
      r.setPixelRatio(Q.pr); r.setSize(this.w, this.h, false);
    }
    r.render(this.scene, camera);
    this.ctx.clearRect(0, 0, cv.width, cv.height);
    this.ctx.drawImage(r.domElement, 0, 0, cv.width, cv.height);
  }

  // テスト・サムネイル用: 指定の位置で止めて描く
  pose(t) { this.t = t; this.playing = false; this.syncPlay(); this.frame(0); }
}

function webglOK() {
  try { const c = document.createElement('canvas'); return !!(c.getContext('webgl2') || c.getContext('webgl')); } catch (_) { return false; }
}

export async function mount(el, opts = {}) {
  if (el.__j3d) return el.__j3d;
  const p = new Player(el, opts);
  el.__j3d = p;
  if (!webglOK()) { p.$('.j3d-load').textContent = 'この端末では3D表示に対応していません'; return p; }
  try { await p.init(); } catch (err) { p.$('.j3d-load').textContent = '3Dを表示できませんでした'; console.error('[jiju-3d]', err); }
  return p;
}

// ページ内の .jiju-3d を、画面に近づいたら組み立てる
function auto() {
  const els = [...document.querySelectorAll('.jiju-3d[data-ex]:not([data-manual])')];
  if (!els.length) return;
  const io = new IntersectionObserver((es) => {
    for (const e of es) if (e.isIntersecting) { io.unobserve(e.target); mount(e.target); }
  }, { rootMargin: '300px' });
  for (const el of els) io.observe(el);
}

if (typeof window !== 'undefined') {
  window.Jiju3D = { mount, MUSCLES, DIM, P };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', auto); else auto();
}
